(ns ductile.schema
  (:require #?(:clj [ductile.cas])
            [clojure.edn :as edn]
            [clojure.set :as set]
            [clojure.string :as str]
            [ductile.dependent-selects :as dependent-selects]
            [ductile.scope-auth :as scope-auth]
            [ductile.ui.locale :as locale :refer [tr]]
            [ductile.workflow.form-recorder :as rec]
            [malli.core :as m]
            [malli.registry :as mr]
            [malli.transform :as mt]
            [malli.util :as mu])
  #?(:clj (:import (java.time YearMonth)
                   (java.time.format DateTimeParseException))))

(defn str->inst [s]
  #?(:clj s
     :cljs (let [d (js/Date. (js/Date.parse (str s (apply str (drop (count s) "0000-00-00T00:00:00.000-00:00")))))]
             (when-not (NaN? d) d))))

(defn inst->str [s]
  #?(:clj s
     :cljs (some-> s (.toISOString) (subs 0 (count "0000-00-00T00:00:00")))))

(defn str->keyword [s]
  (let [s' (str/replace (str s) #"^\:+" "")]
    (when (seq s') (keyword s'))))

(defn *->str [x]
  (cond (inst? x) (inst->str x)
        (string? x) (str x)
        :else (pr-str x)))

(defn double->bigdec [v]
  #?(:cljs v
     :clj (when v
            (if (decimal? v)
              v
              (bigdec (format "%.2f" (double v)))))))

(defn bigdec->double [v]
  #?(:clj (when v (double v))
     :cljs v))

(defn coerce [schema value]
  (if (nil? schema)
    value
    (m/coerce schema value
              (mt/transformer
               {:encoders
                {:inst inst->str
                 :string *->str
                 :edn edn/read-string
                 'decimal? bigdec->double}
                :decoders
                {:inst str->inst
                 :keyword str->keyword
                 :string *->str
                 :edn edn/read-string
                 'decimal? double->bigdec}}
               (mt/string-transformer)))))

(defn coerce-or [schema value & [or-value]]
  (try (coerce schema value)
       (catch #?(:cljs :default :clj Exception) _ or-value)))

(defn json-coerce
  "Coerce JSON-like values to match schema types (e.g. vecs to sets).
   Used by both frontend validation and backend save pipeline."
  [schema value]
  (m/decode schema value (mt/json-transformer)))

(defonce !malli-schema-registry
  (atom {}))

(mr/set-default-registry!
 (mr/composite-registry
  (m/default-schemas)
  (mu/schemas)
  (mr/mutable-registry !malli-schema-registry)))

(defn register! [type ?schema]
  (swap! !malli-schema-registry assoc type ?schema))

(register! :edn (m/-simple-schema {:type :edn
                                   :pred (constantly true)}))


(register! :ductile.datomic.api/txd [:sequential :any])
(register! :inst (m/-simple-schema
                  {:type :inst
                   :pred inst?
                   :type-properties {:error/fn (fn [& _] (tr "should be timestamp"))}}))
(register! :string+ (m/-simple-schema
                     {:type :string+
                      :pred (every-pred string? (complement str/blank?))
                      :type-properties {:error/fn (fn [& _] (tr "should be a non-empty string"))}}))

(doseq [datetime-schema
        #{:entry/datetime
          :entry/expected-datetime
          :fahrzeug/storage-care-start-datetime
          :service/completed-at
          :fahrzeug/expected-customer-delivery-datetime}]
  (register! datetime-schema :inst))


(def email [:re {:error/fn (fn [{:keys [value]} _]
                             (tr "%1 should be a valid email address" value))}
            #"^[\w\-\.]+@([\w-]+\.)+[\w-]{2,}"])

(register! :email email)

(doseq [[key schema]
        {:address/name :string+
         :address/street :string+
         :address/postcode :string+
         :address/city :string+
         :address/email :email
         :elog/adresse-id :string+}]
  (register! key schema))

(register! :ident ident?)

(def iso8601-datetime-string
  [:re #"\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z)"])

(def date-string [:re #"\d{4}-[01]\d-[0-3]\d"])

(def ars-id-regex #"[A-Z0-9]{20,33}")
(register! :ars/id [:re ars-id-regex])
(def vin-regex #"^[A-HJ-NPR-Z0-9]{17}$")
(register! :vehicle/vin [:re vin-regex])
(def full-vin? (m/validator :vehicle/vin))
(register! :wheel-set/vin :vehicle/vin)

(def partial-vin-regex #"^[A-HJ-NPR-Z0-9]{7,17}$")
(register! :partial-vin [:re partial-vin-regex])
(def partial-vin? (m/validator :partial-vin))

(def compound-names
  #_(->> (ductile.datomic.api/q '[:find [?cn ...] :where [_ :compound/name ?cn]] (dev/db))
         sort
         vec)
  ["ARVAL-P3"
   "Castiglione delle Stiviere"
   "Civitavecchia"
   "Eitting"
   "Fiumicino"
   "Frosinone"
   "Hodenhagen"
   "Klitschmar"
   "Knautnaundorf"
   "Krefeld"
   "Lonato"
   "Piadena"
   "Ravenna"
   "Regensburg"
   "Riedstadt"
   "Tor Cervara"
   "Wolnzach"])

(register! :compound/name (into [:enum] compound-names))

(def sixt-serial-number-regex #"\d{15}")
(register! :sixt.tracker/serial-number [:re sixt-serial-number-regex])
(register! :sixt.tag/serial-number [:re sixt-serial-number-regex])
(register! :europcar.tracker/serial-number [:re #"\d{10}"])

;; UUID string format for tool parameters and external IDs
;; Uses RFC 4122 format validation via regex
(def ^:private uuid-regex #"^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$")

(defn- uuid-string? [s]
  (and (string? s) (re-matches uuid-regex s)))

(register! :uuid-string (m/-simple-schema
                         {:type :uuid-string
                          :pred uuid-string?
                          :type-properties {:error/message "should be a valid UUID string"}}))

#?(:clj (register! :ductile/db [:fn (partial instance? datomic.db.Db)]))
#?(:clj (register! :ductile.datomic/log [:fn (partial instance? datomic.log.LogValue)]))
#?(:clj (register! :ductile/cas [:fn (partial satisfies? ductile.cas/ICAS)]))
(register! :ductile/system :map)

(register! :cas/multihash :string+)
(register! :cas/entity [:map :cas/multihash])

(register! :file/mime-type :string)
(register! :file/name :string)

(register! :cas/file [:map
                      :cas/multihash
                      [:file/mime-type {:optional true}]
                      [:file/name {:optional true}]])

(register! :ductile/id :uuid)
(register! :data/clojure :any)

(register! :ductile/fahrzeug-entity [:map
                                     :ductile/id
                                     [:fahrzeug/vehicle :map]])

(register! :scan/location [:map
                           [:location/longitude number?]
                           [:location/latitude number?]])

(register! :rec/fn [:fn rec/workflow-fn?])

(register! :load/id :string+)

#?(:clj
   (defn string->year-month [s]
     (if (string? s)
       (try (YearMonth/parse s)
            (catch DateTimeParseException e nil))
       s)))

(register! :time/year-month (m/-simple-schema {:type :time/year-month
                                               :pred #?(:clj (partial instance? YearMonth)
                                                        :cljs #(and (string? %)
                                                                    (some? (re-matches #"\d{4}-\d{2}" %))))}))

#?(:clj
   (def string->year-month-transformer (mt/transformer {:name :string->year-month-transformer
                                                        :decoders {:time/year-month string->year-month}})))

(register! :transport/assumed-vehicles-count [:int {:min 1}])

(def transport-mode-schema
  (m/schema [:enum :mode/truck :mode/train :mode/ship :mode/airplane :mode/own-wheels :mode/unknown]))
(register! :transport/mode transport-mode-schema)

(def engine-type-schema
  (m/schema [:enum :engine.type/internal-combustion :engine.type/mild-hybrid :engine.type/full-hybrid :engine.type/plug-in-hybrid :engine.type/electric]))
(register! :vehicle/engine-type engine-type-schema)

(def italian-provinces ["AG" "AL" "AN" "AO" "AR" "AP" "AT" "AV" "BA" "BT"
                        "BL" "BN" "BG" "BI" "BO" "BZ" "BS" "BR" "CA" "CL"
                        "CB" "CE" "CT" "CZ" "CH" "CO" "CS" "CR" "KR" "CN"
                        "EN" "FM" "FE" "FI" "FG" "FC" "FR" "GE" "GO" "GR"
                        "IM" "IS" "SP" "AQ" "LT" "LE" "LC" "LI" "LO" "LU"
                        "MC" "MN" "MS" "MT" "ME" "MI" "MO" "MB" "NA" "NO"
                        "NU" "OR" "PD" "PA" "PE" "PR" "PV" "PG" "PS" "PU" "PC"
                        "PI" "PT" "PN" "PZ" "PO" "RG" "RA" "RC" "RE" "RI"
                        "RN" "RM" "RO" "SA" "SS" "SU" "SV" "SI" "SR" "SO"
                        "TA" "TE" "TR" "TO" "TP" "TN" "TV" "TS" "UD" "VA"
                        "VE" "VB" "VC" "VR" "VV" "VI" "VT"])
(def italian-regions ["Abruzzo" "Basilicata" "Calabria" "Campania" "Emilia-Romagna" "Friuli-Venezia Giulia" "Lazio"
                      "Liguria" "Lombardia" "Marche" "Molise" "Piemonte" "Puglia" "Sardegna" "Sicilia" "Toscana"
                      "Trentino-Alto Adige" "Umbria" "Valle d'Aosta" "Veneto"])


(def locales
  [:de :it])

(def destination-schema
  (m/schema
   [:multi {:dispatch (comp :country/code :address/country)}
    ["ITA" [:map
            [:address/street]
            [:address/postcode]
            [:address/city]
            [:address/province-code (into [:enum] italian-provinces)]
            [:address/region (into [:enum] italian-regions)]]]
    [::m/default [:map
                  [:address/street]
                  [:address/postcode]
                  [:address/city]]]]))

(register! :fahrzeug/destination destination-schema)

(def dependent-selects (m/-simple-schema
                        {:type :dependent-selects
                         :pred (constantly true)
                         :property-pred (fn [props]
                                          (fn [v]
                                            (dependent-selects/valid-selection?
                                             (:dependent-selects/choice-tree props)
                                             v)))}))
(register! :dependent-selects dependent-selects)

(def pred-schema :any)
(register! :ductile/pred pred-schema)
(def preds-schema [:vector :ductile/pred])
(register! :ductile/preds preds-schema)
(def service-consequence-schema
  [:map
   [:service/code :string+]
   [:service/consequence-preds {:optional true} :ductile/preds]])

(def notification-consequence-schema
  [:map
   [:notification/kind :keyword]
   [:service/consequence-preds {:optional true} :ductile/preds]])

(def consequence-schema
  [:or service-consequence-schema notification-consequence-schema])

(def scope-schema (into [:enum] scope-auth/all-scopes))
(register! :ductile.lambda/scope scope-schema)
(def scopes-schema [:or scope-schema [:set scope-schema]])
(register! :ductile.lambda/scopes scopes-schema)
(declare service-schema)
(def duration-schema [:tuple [:int {:min 1}] [:enum :minutes :hours :days :weeks :months]])
(def schedule-schema [:map
                      [:cycle duration-schema]
                      [:->cycle-start {:optional true} ifn?]
                      [:at-entry? {:optional true} :boolean]
                      [:cycle-limit {:optional true} :int]])

(m/validate schedule-schema {:cycle [1 :days]})
(def event-kind-schema [:enum
                        :service.event/assigned
                        :service.event/canceled
                        :service.event/completed
                        :service.event/invoiced
                        :service.event/ordered
                        :service.event/undone
                        :service.event/edited])

(def service-default-attrs-schema
  "Allows to specify default values for `:ui/attrs` used in `:ui/fields`.
  Any `ductile.workflow.form-recorder/record`ed function gets replaced by its result when called with the `workflow-map` (~= fahrzeug),
  which allows to specify default values dependent on the fahrzeug."
  [:map-of :keyword :any])

(register! :service/code :string+)
(register! :service/id :keyword)
(register! :service/template [:map :service/id])

(def requested-keys-schema-definitions
  ;; keys come from
  #_(into []
          (comp (map :db/ident)
                (filter #(= "requested" (namespace %))))
          ductile.datomic.schema/schema)
  {:requested/fuel-liters [:int {:min 0}]
   :requested/fuel-percentage [:int {:min 0 :max 100}]
   :requested/charge-percentage [:int {:min 0 :max 100}]
   :requested/charge-kwh [:double {:min 0}]
   :requested/pressure-bar [:double {:min 0}]})

(doseq [[k schema-definition] requested-keys-schema-definitions]
  (register! k schema-definition))

(def requested-keys (keys requested-keys-schema-definitions))

(def service-order-schema
  [:map
   [:service/code {:optional true}]
   [:service/template {:optional true}]
   [:requested/fuel-liters {:optional true}]
   [:requested/fuel-percentage {:optional true}]
   [:requested/charge-kwh {:optional true}]
   [:requested/charge-percentage {:optional true}]
   [:requested/pressure-bar {:optional true}]])

(def service-schema
  [:and [:merge
         [:map #_{:closed true}
          [:service/code {:optional true} :string+]
          [:service/template {:optional true} [:map [:service/id keyword?]]]
          [:service/preds {:optional true} preds-schema]

          [:service/active? {:optional true} :boolean]
          [:ductile/id {:optional true} :uuid]
          [:ductile/created-at {:optional true} :inst]
          [:ductile/user {:optional true} :any]

          [:service/vehicle-preds {:optional true} preds-schema]
          [:service/contract-preds {:optional true} preds-schema]
          [:service/operational-preds {:optional true} preds-schema]

          [:service/schedule {:optional true} schedule-schema]
          [:service/required-scope {:optional true} scopes-schema]
          [:service/optionality {:optional true} [:enum :optionality/must
                                                  :optionality/should
                                                  :optionality/can]]

          [:service/kind {:optional true} [:enum :ductile.workflow.dsl/group]]
          [:service/services {:optional true} coll?]

          [:service/available-units {:optional true} [:set :keyword]]
          [:service/unit {:optional true} :keyword]

          [:service/postprocess {:optional true} ifn?]
          [:service/txd {:optional true} [:vector :any]]
          ;; custom lambda handler to call when `perform` is pressed
          ;; instead of `ductile.workflow/perform-service`
          [:service/on-perform {:optional true} :symbol]

          ;; detail text that shows next to the service name
          [:service/detail {:optional true} :string+]
          [:event/kind {:optional true} event-kind-schema]

          [:service/consequences {:optional true} [:sequential consequence-schema]] ;;sometimes list, sometimes vector
          [:ui/view {:optional true} :symbol]
          [:ui/fields {:optional true} [:vector :any]]
          [:ui/subtitle {:optional true} [:or :keyword :string+]]
          [:ui/cancel-fields {:optional true} [:vector :any]]
          [:ui/hide-perform-button? {:optional true} :boolean]

          [:service/default-attrs {:optional true} service-default-attrs-schema]

          ;; Controls processing of the `:ductile/data` attribute.
          ;; The currently only implemented option is `:all` which
          ;; stores all attributes in the `:ductile/data` edn
          ;; string.
          [:service/ductile-data-attrs {:optional true} [:enum :all]]

          [:service/scannable? {:optional true} :boolean]

          ;; This improves our arrival scan by making a backend call using a new
          ;; `ductile.workflow/fahrzeug-with-scan-match`lambda before marking a vehicle as
          ;; arrived. This allows folks to see the effect of a scan and correct it in
          ;; case the scan detected a wrong vin. This should hopefully reduce the
          ;; number of VIN corrections we need to manually perform.
          [:service/augment-scan? {:optional true} :boolean]

          [:service/take-first-child-service-as-active? {:optional true} :boolean]

          ;; TODO: logic that uses self-completing attribute isn't implemented
          [:service/self-completing {:optional true} :boolean]

          ;; When false, entry service won't ask if the vehicle is used or new?
          ;; (can be removed once old workflows are migrated to fields ui)
          [:service/record-old-or-new? {:optional true} :boolean]
          [:service/equipment-list {:optional true} [:or [:vector :string+] [:set :string+]]] ;; set in italy, vector in germany
          ;; Reparking services will be shown next to the parking
          ;; location in the compound-ops vehicle page (Italy specific)
          [:service/reparking {:optional true} :boolean]

          ;; Change the label shown on the button to confirm the service
          [:service/confirm-label {:optional true} :string+]]
         service-order-schema]
   [:or
    [:map
     [:service/code {:optional true}]
     :service/template]
    [:map
     :service/code]]])

(def db-ident-schema
  [:map [:db/ident :keyword]])

(def fahrzeug-notification-schema
  [:map
   [:ductile/id {:optional true} :uuid]
   [:notification/kind {:optional true} :keyword]
   [:notification/status {:optional true} :keyword]
   [:notification/sent-at {:optional true} :inst]
   [:cas/multihash {:optional true} :cas/multihash]])

(def fahrzeug-comment-schema
  [:map
   [:ductile/id {:optional true} :uuid]
   [:cas/multihash {:optional true} :cas/multihash]
   [:ductile/timestamp {:optional true} :inst]
   [:ductile.comment/kind {:optional true} db-ident-schema]])

(def fahrzeug-country-schema
  [:map
   [:country/code {:optional true} :string+]])

(def fahrzeug-company-schema
  [:map
   [:company/name {:optional true} :string+]])

(def fahrzeug-location-schema
  [:map
   [:ductile/id {:optional true} :uuid]
   [:compound/company {:optional true} fahrzeug-company-schema]
   [:compound/name {:optional true}]
   [:location/name {:optional true} :string+]
   [:address/name {:optional true}]
   [:address/street {:optional true}]
   [:address/postcode {:optional true}]
   [:address/city {:optional true}]
   [:address/province-code {:optional true} :string+]
   [:address/region {:optional true} :string+]
   [:elog/adresse-id {:optional true}]
   [:external.bmw/location-codes {:optional true} [:vector :string+]]
   [:external.kia/location-codes {:optional true} [:vector :string+]]
   [:external.omoda-jaecoo/location-codes {:optional true} [:vector :string+]]
   [:address/country {:optional true} fahrzeug-country-schema]
   [:location/types {:optional true} [:vector db-ident-schema]]
   [:location/latitude {:optional true} number?]
   [:location/longitude {:optional true} number?]
   [:location/delivery-modalities {:optional true} :any]])

(def fahrzeug-transport-carrier-schema
  [:map
   [:carrier/code {:optional true} :string+]
   [:location/types {:optional true} [:vector db-ident-schema]]
   [:external.bmw/forwarder-id {:optional true} :string+]
   [:company/name {:optional true} :string+]
   [:ductile/id {:optional true} :uuid]
   [:address/name {:optional true}]
   [:address/street {:optional true}]
   [:address/postcode {:optional true}]
   [:address/city {:optional true}]
   [:report/emails {:optional true} [:vector :email]]
   [:transport/license-plate {:optional true} :string+]])

(register! :document/number :string+)

(def fahrzeug-bill-of-lading-schema
  [:map
   [:destination/location {:optional true} fahrzeug-location-schema]
   [:ductile/created-at {:optional true} :inst]
   [:document/number {:optional true}]
   [:ductile/id {:optional true} :uuid]
   [:cas/multihash {:optional true} :cas/multihash]
   [:file/mime-type {:optional true} :file/mime-type]
   [:file/name {:optional true} :file/name]])


(register! :truck/number :string+)
(register! :truck/valid-from :inst)
(register! :truck/valid-until :inst)

(def fahrzeug-transporter-schema
  [:map
   [:ductile/id {:optional true}]
   [:truck/number {:optional true}]
   [:transport/license-plate {:optional true} :string+]
   [:transport/driver {:optional true} [:map
                                        [:ductile/id {:optional true} :uuid]
                                        [:transport/driver-name {:optional true} :string+]]]
   [:transport/carrier {:optional true} fahrzeug-transport-carrier-schema]])

(def fahrzeug-parking-spot-schema
  [:map
   [:parking-spot/row {:optional true} :int]
   [:parking-spot/place {:optional true} :int]
   [:parking-spot/zone {:optional true} [:or
                                         [:map [:parking-zone/name {:optional true} :string+]]
                                         :string+]]
   [:db/id {:optional true} :int]])

(doseq [[k v] {:user/name :string+
               :user/email :email}]
  (register! k v))

(def fahrzeug-service-schema
  [:map
   [:ductile/id {:optional true} :uuid]
   [:service/code {:optional true} :service/code]
   [:service/completed-at {:optional true} :inst]
   [:service/template {:optional true} [:map [:service/id :keyword]]]
   [:event/undone-at {:optional true} :inst]
   [:target/events {:optional true} [:vector :any]]
   [:ductile/created-at {:optional true} :inst]
   [:ductile/user {:optional true} [:map [:user/email :email]]]
   [:ductile/note {:optional true} :string]
   [:edi/provider {:optional true} db-ident-schema]
   [:ductile/notifications {:optional true} [:vector fahrzeug-notification-schema]]
   [:ductile/data {:optional true} :any]
   [:location/latitude {:optional true} number?]
   [:location/longitude {:optional true} number?]
   [:service/input-data {:optional true} :any]
   [:event/kind {:optional true} db-ident-schema]
   [:service/quantity {:optional true} number?]
   [:service/scans {:optional true} [:vector [:map
                                              [:cas/multihash {:optional true} :cas/multihash]
                                              [:ductile/created-at {:optional true} :inst]
                                              [:file/mime-type {:optional true} :file/mime-type]]]]
   [:fuel/type {:optional true} db-ident-schema]
   [:service/scheduled-for-datetime {:optional true} :inst]
   [:service/schedule-cycle-number {:optional true} :int]
   [:vehicle/license-plate {:optional true} :string+]
   [:document/vehicle-registration-certificate-1 {:optional true} :cas/file]
   [:vehicle/equipment {:optional true} :any]
   [:vehicle.status/high-voltage-battery-status {:optional true} db-ident-schema]
   [:vehicle.status/high-voltage-battery-charge {:optional true} number?]
   [:vehicle.status/battery-charge {:optional true} number?]
   [:vehicle.status/tire-pressure {:optional true} number?]
   [:service/unit {:optional true} db-ident-schema]
   [:billing/mode {:optional true} db-ident-schema]
   [:billing/invoice {:optional true} [:map [:invoice/invoice-number {:optional true} :string+]]]
   [:service/contract {:optional true} [:map [:contract/name {:optional true} :string+]]]
   [:elog/service-code {:optional true} :string+]
   [:ductile/comments {:optional true} [:vector fahrzeug-comment-schema]]
   [:service/result {:optional true} :any]
   [:destination/location {:optional true} fahrzeug-location-schema]
   [:car-on-sale/vehicle-class {:optional true} db-ident-schema]
   [:bca/vehicle-class {:optional true} db-ident-schema]
   [:sixt.tracker/serial-number {:optional true} :sixt.tracker/serial-number]
   [:sixt.tag/serial-number {:optional true} :sixt.tag/serial-number]
   [:sixt.accessory/placement {:optional true} :string]
   [:service/active? {:optional true} :boolean]
   [:ui/view {:optional true} :symbol]
   [:service/optionality {:optional true} [:enum :optionality/must
                                           :optionality/should
                                           :optionality/can]]
   [:service/reparking {:optional true} :boolean]
   [:service/vehicle-preds {:optional true} preds-schema]
   [:service/contract-preds {:optional true} preds-schema]
   [:service/operational-preds {:optional true} preds-schema]
   [:service/schedule {:optional true} schedule-schema]
   [:service/default-attrs {:optional true} service-default-attrs-schema]
   [:service/scannable? {:optional true} :boolean]
   [:service/consequences {:optional true} [:sequential consequence-schema]]
   [:service/services {:optional true} [:vector :any]]
   [:workflow/code {:optional true} :string+]
   [:workflow/open-service-codes {:optional true} [:or [:vector :service/code] [:set :service/code] nil?]]
   [:requested/fuel-liters {:optional true} :requested/fuel-liters]
   [:requested/fuel-percentage {:optional true} :requested/fuel-percentage]
   [:requested/charge-kwh {:optional true} :requested/charge-kwh]
   [:requested/charge-percentage {:optional true} :requested/charge-percentage]
   [:requested/pressure-bar {:optional true} :requested/pressure-bar]])

(def fahrzeug-transport-schema
  [:map
   [:ductile/id {:optional true} :uuid]
   [:edi/provider {:optional true} db-ident-schema]
   [:ductile/data {:optional true} :any]
   [:fahrzeug/customs-number {:optional true} :string+]
   [:fahrzeug/order-number {:optional true} :string+]
   [:transport/cancelled-datetime {:optional true} :inst]
   [:transport/name {:optional true} :string+]
   [:transport/number {:optional true} :string+]
   [:transport/load-number {:optional true} :string+]
   [:transport/bills-of-lading {:optional true} [:vector fahrzeug-bill-of-lading-schema]]
   [:transport/mode {:optional true} :transport/mode]
   [:transport/carrier {:optional true} fahrzeug-transport-carrier-schema]
   [:transport/forwarder {:optional true} fahrzeug-transport-carrier-schema]
   [:transport/transporter {:optional true} fahrzeug-transporter-schema]
   [:transport/assumed-vehicles-count {:optional true} :int]
   [:entry/unloading-line {:optional true} :string]
   [:external.bmw/carrier-code {:optional true} :string+]
   [:external.bmw/spediteur {:optional true} :string+]
   [:eurolog.bmw/last-unblocked {:optional true} :inst]
   [:transport/license-plate {:optional true} :string+]
   [:transport/carrier-negotiated-amount {:optional true} number?]
   [:transport/customer-negotiated-amount {:optional true} number?]
   [:transport/preparable-at {:optional true} :inst]
   [:transport/ready-at {:optional true} :inst]
   [:transport/finalized-at {:optional true} :inst]
   [:transport/deleted-at {:optional true} :inst]
   [:load/id {:optional true} :load/id]
   [:load/entered-with {:optional true} [:map [:ductile/id :uuid]]]
   [:load/exited-with {:optional true} [:map [:ductile/id :uuid]]]
   [:transport/driver-name {:optional true} :string+]
   [:transport/customer-load-number {:optional true} :string+]
   [:takeover/conditions {:optional true} [:vector db-ident-schema]]
   [:ductile/created-at {:optional true} :inst]
   [:arrival/datetime {:optional true} :inst]
   [:entry/datetime {:optional true} :inst]
   [:entry/expected-datetime {:optional true} :inst]
   [:exit/datetime {:optional true} :inst]
   [:exit/expected-datetime {:optional true} :inst]
   [:external.bmw/usage {:optional true} :string+]
   [:vehicle/blocks {:optional true} [:vector db-ident-schema]]
   [:origin/location {:optional true} fahrzeug-location-schema]
   [:destination/location {:optional true} fahrzeug-location-schema]
   [:transport/loading-line {:optional true} fahrzeug-parking-spot-schema]])

(def fahrzeug-contract-schema
  [:map
   [:contract/name {:optional true} :string+]
   [:contract/customer {:optional true} [:map [:customer/name {:optional true} :string+]]]
   [:contract/parent-contract {:optional true} [:map
                                                [:contract/name {:optional true} :string+]
                                                [:contract/customer {:optional true} [:map [:customer/name {:optional true} :string+]]]]]])

(def fahrzeug-vehicle-schema
  [:map
   [:vehicle/vin {:optional true} :vehicle/vin]
   [:vehicle/height-mm {:optional true} :int]
   [:vehicle/length-mm {:optional true} :int]
   [:vehicle/weight {:optional true} :int]
   [:vehicle/width-mm {:optional true} :int]
   [:vehicle/import-value {:optional true} number?]
   [:vehicle/import-document-number {:optional true} :string+]
   [:vehicle/accessories {:optional true} [:vector :any]]
   [:vehicle/equipment {:optional true} :any]
   [:vehicle/engine-type {:optional true} db-ident-schema]
   [:vehicle/mileage {:optional true} :int]
   [:vehicle/model {:optional true} :string]
   [:vehicle/volume {:optional true} number?]
   [:vehicle/new? {:optional true} :boolean]
   [:vehicle/scheduled-for-lpg-conversion? {:optional true} :boolean]
   [:vehicle/color {:optional true} [:map
                                     [:external.bmw/color-code {:optional true} :string+]
                                     [:external.kia/color-code {:optional true} :string+]
                                     [:color/description {:optional true} :string+]]]
   [:vehicle/blocks {:optional true} [:vector db-ident-schema]]
   [:vehicle/make {:optional true} [:map [:make/name {:optional true} :string+]]]
   [:vehicle/model-info {:optional true} [:map
                                          [:vehicle/model {:optional true} :string]
                                          [:ductile/data {:optional true} :any]
                                          [:external.bmw/model-code {:optional true} :string+]
                                          [:external.kia/model-code {:optional true} :string+]
                                          [:external.omoda-jaecoo/model-code {:optional true} :string+]
                                          [:vehicle/cylinder-capacity {:optional true} number?]
                                          [:fuel/type {:optional true} db-ident-schema]
                                          [:vehicle/engine-type {:optional true} db-ident-schema]]]])

(defn- fahrzeug-prev-schema [depth]
  (let [entry [:map {:closed true}
               [:ductile/id {:optional true} :uuid]
               [:entry/datetime {:optional true} :inst]
               [:ductile/location {:optional true} [:map
                                                    [:compound/name {:optional true}]]]
               [:fahrzeug/services {:optional true} [:vector fahrzeug-service-schema]]]]
    (if (pos? depth)
      (conj entry [:fahrzeug/prev {:optional true} (fahrzeug-prev-schema (dec depth))])
      entry)))

(def fahrzeug-fat-schema
  [:map
   [:ductile/id {:optional true} :uuid]
   [:ductile/data {:optional true} :any]
   [:ductile/notifications {:optional true} [:vector fahrzeug-notification-schema]]
   [:acceptance.conditions/day-or-night {:optional true} :boolean]
   [:acceptance.conditions/ice-&-snow? {:optional true} :boolean]
   [:ars/id {:optional true} :ars/id]
   [:fahrzeug/damages {:optional true} [:vector [:map [:ductile/id {:optional true} :uuid]]]]
   [:arrival-scan/created {:optional true} db-ident-schema]
   [:elog/fahrzeug-id {:optional true} :string+]
   [:fahrzeug/customs-number {:optional true} :string+]
   [:fahrzeug/order-number {:optional true} :string+]
   [:exit/datetime {:optional true} :inst]
   [:exit/expected-datetime {:optional true} :inst]
   [:entry/datetime {:optional true} :inst]
   [:entry/expected-datetime {:optional true} :inst]
   [:entry/mode {:optional true} :keyword]
   [:storage/entry-datetime {:optional true} :inst]
   [:storage/exit-datetime {:optional true} :inst]
   [:inspection/company-name {:optional true} :string+]
   [:fahrzeug/expected-customer-delivery-datetime {:optional true} :inst]
   [:vehicle/dispatchable-without-call-off? {:optional true} :boolean]
   [:fahrzeug/exit-delivery-datetime {:optional true} :inst]
   [:fahrzeug/exit-pickup-datetime {:optional true} :inst]
   [:fahrzeug/delivery-dealer-code-raw {:optional true} :string+]
   [:fahrzeug/storage-care-start-datetime {:optional true} :inst]
   [:external.bmw/usage {:optional true} :string+]
   [:dispo/available-datetime {:optional true} :inst]
   [:transport/number {:optional true} :string+]
   [:transport/name {:optional true} :string+]
   [:transport/license-plate {:optional true} :string+]
   [:transport/driver-name {:optional true} :string+]
   [:vehicle/license-plate {:optional true} :string+]
   [:document/vehicle-registration-certificate-1 {:optional true} :cas/file]
   [:vehicle/mileage {:optional true} :int]
   [:vehicle/model {:optional true} :string]
   [:vehicle/new? {:optional true} :boolean]
   [:vehicle/accident? {:optional true} :boolean]
   [:vehicle/depowered? {:optional true} :boolean]
   [:vehicle/dirty-outside? {:optional true} :boolean]
   [:vehicle/permanently-quarantined? {:optional true} :boolean]
   [:vehicle/transport-mode-active? {:optional true} :boolean]
   [:vehicle/leaky? {:optional true} :boolean]
   [:vehicle/non-drivable? {:optional true} :boolean]
   [:vehicle.status/fuel-tank-liters {:optional true} number?]
   [:vehicle.status/mineral-oil-liters {:optional true} number?]
   [:vehicle.status/high-voltage-battery-charge {:optional true} number?]
   [:vehicle.status/high-voltage-battery-charge-kwh {:optional true} number?]
   [:vehicle.status/battery-charge {:optional true} number?]
   [:vehicle.status/battery-voltage {:optional true} number?]
   [:vehicle.status/battery-status {:optional true} db-ident-schema]
   [:battery/disconnected? {:optional true} :boolean]
   [:vehicle.status/high-voltage-battery-status {:optional true} db-ident-schema]
   [:vehicle.status/high-voltage-battery-thermal-check {:optional true} db-ident-schema]
   [:vehicle.status/tire-pressure {:optional true} number?]
   [:fahrzeug/assignable-contracts {:optional true} [:vector [:map [:contract/name {:optional true} :string+]]]]
   [:fahrzeug/contract {:optional true} fahrzeug-contract-schema]
   [:fahrzeug/next {:optional true} [:map
                                     [:ductile/id {:optional true} :uuid]
                                     [:ductile/location {:optional true} [:map [:compound/name {:optional true}]]]]]
   [:fahrzeug/prev {:optional true} (fahrzeug-prev-schema 2)]
   [:vehicle/engine-type {:optional true} [:or :vehicle/engine-type db-ident-schema]]
   [:ductile/comments {:optional true} [:vector fahrzeug-comment-schema]]
   [:entry/transport {:optional true} fahrzeug-transport-schema]
   [:exit/transport {:optional true} fahrzeug-transport-schema]
   [:entry/transport-order {:optional true} fahrzeug-transport-schema]
   [:exit/transport-order {:optional true} fahrzeug-transport-schema]
   [:fahrzeug/destination {:optional true} fahrzeug-location-schema]
   [:fahrzeug/workflow {:optional true} [:map [:workflow/code {:optional true} :string+]]]
   [:fahrzeug/priorities {:optional true} [:vector db-ident-schema]]
   [:ductile/location {:optional true} fahrzeug-location-schema]
   [:fahrzeug/customer {:optional true} [:map [:customer/name {:optional true} :string+]]]
   [:fahrzeug/parking-spot {:optional true} fahrzeug-parking-spot-schema]
   [:fahrzeug/vehicle {:optional true} fahrzeug-vehicle-schema]
   [:fahrzeug/services {:optional true} [:vector fahrzeug-service-schema]]
   [:external.arval/transport-name {:optional true} :string+]
   [:external.arval/leasing-contract-number {:optional true} :string+]
   [:external.arval/return-mode {:optional true} :string+]
   [:external.arval/invoice-code {:optional true} :string+]
   [:car-on-sale/vehicle-class {:optional true} db-ident-schema]
   [:bca/vehicle-class {:optional true} db-ident-schema]

   ;; commonly lifted / derived keys on fat-fahrzeug
   [:vehicle/vin {:optional true} :vehicle/vin]
   [:make/name {:optional true} :string+]
   [:contract/name {:optional true} :string+]
   [:contract/names {:optional true} [:vector :string+]]
   [:contract/framework-contract {:optional true} :string+]
   [:customer/name {:optional true} :string+]
   [:compound/name {:optional true}]
   [:country/code {:optional true} :string+]
   [:color/description {:optional true} :string+]
   [:color/gradient-from {:optional true} :string+]
   [:color/gradient-to {:optional true} :string+]
   [:external.bmw/color-code {:optional true} :string+]
   [:external.bmw/model-code {:optional true} :string+]
   [:external.kia/model-code {:optional true} :string+]
   [:external.omoda-jaecoo/model-code {:optional true} :string+]
   [:parking-zone/name {:optional true} :string+]
   [:parking-spot {:optional true} :string+]
   [:vehicle/blocks {:optional true} [:vector :keyword]]
   [:vehicle/dimensions {:optional true} :string+]
   [:vehicle/engine-type-from-model-info? {:optional true} :boolean]
   [:fuel/type {:optional true} :keyword]
   [:service.event/completed {:optional true} [:vector fahrzeug-service-schema]]
   [:service.event/canceled {:optional true} [:vector fahrzeug-service-schema]]
   [:service.event/invoiced {:optional true} [:vector fahrzeug-service-schema]]
   [:service.event/ordered {:optional true} [:vector fahrzeug-service-schema]]
   [:service.event/undone {:optional true} [:vector fahrzeug-service-schema]]
   [:service.event/edited {:optional true} [:vector fahrzeug-service-schema]]
   [:workflow/code {:optional true} :string+]
   [:workflow/open-service-codes {:optional true} [:or [:vector :service/code] [:set :service/code] nil?]]
   [:workflow/active-service-codes {:optional true} [:set :service/code]]
   [:workflow/cancelled-service-codes {:optional true} [:set :service/code]]
   [:workflow/mandatory-services {:optional true} [:vector fahrzeug-service-schema]]
   [:workflow/optional-services {:optional true} [:vector fahrzeug-service-schema]]
   [:workflow/active-services {:optional true} [:vector fahrzeug-service-schema]]
   [:workflow/services {:optional true} [:vector fahrzeug-service-schema]]
   [:exit/transport-kind {:optional true} :keyword]
   [:exit/transport-info {:optional true} :string]
   [:fahrzeug/customs-clearance {:optional true} :map]
   [:fahrzeug/call-off-datetime {:optional true} :inst]
   [:fahrzeug/available-workflows {:optional true} [:vector :any]]
   [:fahrzeug/assignable-services {:optional true} [:vector :any]]
   [:fahrzeug/assigned-services {:optional true} [:vector :any]]])

(def fahrzeug-fat-top-level-keys
  (->> fahrzeug-fat-schema
       rest
       (keep (fn [entry]
               (when (and (vector? entry) (keyword? (first entry)))
                 (first entry))))
       set))

(register! :fahrzeug/fat fahrzeug-fat-schema)

(def fat-fahrzeug?
  (m/validator :fahrzeug/fat))


(register! :ui/field-view [:enum
                           ; Most common field views
                           :ui.view/text
                           :ui.view/textarea
                           :ui.view/number
                           :ui.view/radio-list
                           :ui.view/checkbox
                           :ui.view/multi-select
                           :ui.view/time
                           :ui.view/date
                           :ui.view/datetime

                           ; Renders in a separate screen/modal; child view is specified via :ui/auxillary-field-view
                           :ui.view/auxiliary

                           :ui.view/combobox
                           :ui.view/dependent-selects
                           :ui.view/file-upload
                           :ui.view/toggle-link
                           :ui.view/photo
                           :ui.view/photos
                           :ui.view/scan
                           :ui.view/tabs
                           :ui.view/entity-diff
                           :ui.view/code
                           :ui.view/data
                           :ui.view/datoms

                           ; Domain-specific field views
                           :ui.view/carrier-ref
                           :ui.view/damages-inside
                           :ui.view/damages-outside
                           :ui.view/fuel-tank-level
                           :ui.view/location-ref
                           :ui.view/accessories-preview
                           :ui.view/data-table
                           :ui.view/storage-care-start-date-view
                           :ui.view/signature
                           :ui.view/parking-spot
                           :ui.view/coordinate-picker
                           :ui.view/scan-vin
                           :ui.view/service-select
                           :ui.view/transport
                           :ui.view/transporter-or-carrier-ref
                           :ui.view/vehicle-list
                           :ui.view/vin
                           :ui.view/delivery-modalities])


(def ui-field-schema [:map
                      [:ui/attr [:or :keyword :symbol]]

                      ; malli type of this attr - often derived from attr (via dsl/attr->field-opts)
                      #_[ductile.workflow.dsl/attr->field-opts]
                      ; TODO only optional when readonly
                      [:ui/schema-type {:optional true} [:or :keyword [:vector :keyword]]]

                      ; What UI view to render - often derived from attr (via dsl/attr->field-opts)
                      #_[ductile.workflow.dsl/attr->field-opts]
                      ; TODO only optional when hidden
                      [:ui/field-view {:optional true} :ui/field-view]

                      ; Optional "Summary" view of selected values, need to add case in ductile.ui.workflow/auxiliary-label
                      ; For colls, defaults to comma-separated translated list
                      ; For maps, uses value under :ui/placeholder
                      [:ui/auxillary-field-view {:optional true} :any]

                      [:ui/title {:optional true} [:or :keyword :string+]]
                      [:ui/placeholder {:optional true} :string]

                      ; default value for this field if there is no value provided by the form-map or the user.
                      [:ui/default-value {:optional true} :any]

                      ; For `:ui/field-view :ui.view/checkbox`, can be a map of any option value to its label
                      [:ui/label {:optional true} [:or :string+ [:map]]]


                      ; Attribute of map value that contains a human-readable label for the selected option
                      ; Vector form supports multiple fallback label keys
                      [:ui/label-key {:optional true} [:or
                                                       :keyword
                                                       [:vector :keyword]]]

                      ; Renders as strikethrough, use when another field's value takes precedence
                      [:ui/overridden-pred {:optional true} :rec/fn]

                      ; Function to validate scanned value. If it returns a truthy value (e.g. the parsed ID), the field gets populated.
                      [:ui.scan/match-scan-fn {:optional true} :rec/fn]

                      [:ui/optional? {:optional true} [:or :rec/fn :boolean]]
                      [:ui/readonly? {:optional true} :boolean]

                      ; Hidden fields are used to roundtrip arbitrary in the top level form-map
                      [:ui/hidden? {:optional true} :boolean]

                      ; Restrict numeric range
                      [:ui/min {:optional true} [:and int? [:or zero? pos?]]]
                      [:ui/max {:optional true} [:and int? [:or zero? pos?]]]

                      ; Shown right-aligned inside the (text, number) input field. Prefer keywords that are auto-translated
                      [:ui/unit {:optional true} [:or :keyword :string+]]

                      ; Input mask, see
                      #_[ductile.ui.components/mask-preset]
                      [:ui/mask {:optional true} :keyword]

                      ; Only show this field when some predicate matches
                      [:ui/pred {:optional true} [:or :keyword :rec/fn]]

                      [:ui/options {:optional true} [:or [:sequential :any] :rec/fn]]

                      ; Optional per-option secondary text for :ui.view/radio-list and :ui.view/multi-select
                      [:ui/descriptions {:optional true} [:map-of :any [:or :keyword :string+]]]

                      ; TODO
                      ; :ui/schema-children [:enum]

                      ; Whether to render a "Toggle all" link in :ui.view/multi-select
                      [:ui/toggle-all? {:optional true} :boolean]

                      ; Don't show a title (use for )
                      [:ui/attached-to-previous-field? {:optional true} :boolean]])
(register! :ui/attr-field ui-field-schema)

(def feather-icon-names
  #_(letfn [(walk [node]
              (lazy-seq
               (concat
                (when (and (= "symbol" (name (:tag node)))
                           (contains? (:attrs node) :id))
                  [(:id (:attrs node))])
                (mapcat walk (:content node)))))]
      (->> "public/images/feather-sprite.svg"
           clojure.java.io/resource
           clojure.java.io/reader
           clojure.data.xml/parse
           walk
           (mapv keyword)
           set))
  #{:activity :airplay :alert-circle :alert-octagon :alert-triangle :align-center :align-justify :align-left :align-right :anchor :aperture :archive :arrow-down-circle :arrow-down-left :arrow-down-right :arrow-down :arrow-left-circle :arrow-left :arrow-right-circle :arrow-right :arrow-up-circle :arrow-up-left :arrow-up-right :arrow-up :at-sign :award :bar-chart-2 :bar-chart :battery-charging :battery :bell-off :bell :bluetooth :bold :book-open :book :bookmark :box :briefcase :calendar :camera-off :camera :cast :check-circle :check-square :check :chevron-down :chevron-left :chevron-right :chevron-up :chevrons-down :chevrons-left :chevrons-right :chevrons-up :chrome :circle :clipboard :clock :cloud-drizzle :cloud-lightning :cloud-off :cloud-rain :cloud-snow :cloud :code :codepen :codesandbox :coffee :columns :command :compass :copy :corner-down-left :corner-down-right :corner-left-down :corner-left-up :corner-right-down :corner-right-up :corner-up-left :corner-up-right :cpu :credit-card :crop :crosshair :database :delete :disc :divide-circle :divide-square :divide :dollar-sign :download-cloud :download :dribbble :droplet :edit-2 :edit-3 :edit :external-link :eye-off :eye :facebook :fast-forward :feather :figma :file-minus :file-plus :file-text :file :film :filter :flag :folder-minus :folder-plus :folder :framer :frown :gift :git-branch :git-commit :git-merge :git-pull-request :github :gitlab :globe :grid :hard-drive :hash :headphones :heart :help-circle :hexagon :home :image :inbox :info :instagram :italic :key :layers :layout :life-buoy :link-2 :link :linkedin :list :loader :lock :log-in :log-out :mail :map-pin :map :maximize-2 :maximize :meh :menu :message-circle :message-square :mic-off :mic :minimize-2 :minimize :minus-circle :minus-square :minus :monitor :moon :more-horizontal :more-vertical :mouse-pointer :move :music :navigation-2 :navigation :octagon :package :paperclip :pause-circle :pause :pen-tool :percent :phone-call :phone-forwarded :phone-incoming :phone-missed :phone-off :phone-outgoing :phone :pie-chart :play-circle :play :plus-circle :plus-square :plus :pocket :power :printer :radio :refresh-ccw :refresh-cw :repeat :rewind :rotate-ccw :rotate-cw :rss :save :scissors :search :send :server :settings :share-2 :share :shield-off :shield :shopping-bag :shopping-cart :shuffle :sidebar :skip-back :skip-forward :slack :slash :sliders :smartphone :smile :speaker :square :star :stop-circle :sun :sunrise :sunset :tablet :tag :target :terminal :thermometer :thumbs-down :thumbs-up :toggle-left :toggle-right :tool :trash-2 :trash :trello :trending-down :trending-up :triangle :truck :tv :twitch :twitter :type :umbrella :underline :unlock :upload-cloud :upload :user-check :user-minus :user-plus :user-x :user :users :video-off :video :voicemail :volume-1 :volume-2 :volume-x :volume :watch :wifi-off :wifi :wind :x-circle :x-octagon :x-square :x :youtube :zap-off :zap :zoom-in :zoom-out})

(def local-icon-names
  #_(set (keys ductile.ui.components-local-icons))
  (into #{:car-wash :tank-water :email :pump :table-columns :csv :binoculars :car :lightbulb :steering-wheel :vin-scanner :check :contract :eye :entered :car-battery-broken :car-battery :pdf :created :eye-slash :chat :fuel :train :spinner :customer :letter-e :invoice :ship :document :brake-warning :gas-pump :parking :damage :edi :oil-can-drip :avatar :tire :exited :barcode-scanner :engine-warning :letter-a :scan :truck :car-wrench :engine :microsoft :garage-car :tire-pressure-warning :excel :academy :panels :disc-brake :oil-temperature :charging-station :oil-can :clean}
        (map (fn [c] (keyword (str "letter-" (char c)))))
        (range (int \a) (inc (int \z)))))
(def react-icon-names
  #_ductile.ui.components/all-icons
  #{:notion-logo :double-arrow-left :text-align-bottom :arrow-left :moon :text-align-right :border-style :line-height :view-vertical :font-style :shuffle :aspect-ratio :pie-chart :keyboard :framer-logo :space-evenly-vertically :file-text :font-roman :thick-arrow-right :list-bullet :space-between-horizontally :share-1 :view-grid :slash :video :question-mark :double-arrow-right :arrow-right :lock-open-1 :thick-arrow-left :cursor-text :home :drag-handle-horizontal :ruler-horizontal :copy :triangle-left :arrow-top-right :font-italic :heart :chevron-left :pin-bottom :space-between-vertically :shadow-inner :speaker-loud :border-right :bookmark :letter-case-lowercase :transform :group :stitches-logo :table :frame :corner-top-left :rotate-counter-clockwise :double-arrow-up :modulz-logo :person :chevron-down :enter-full-screen :chevron-right :corner-top-right :circle-backslash :mask-on :align-left :timer :arrow-up :half-2 :exit :border-dashed :drawing-pin :triangle-up :accessibility :border-solid :font-size :reset :exclamation-triangle :linked-in-logo :dots-vertical :component-placeholder :discord-logo :triangle-down :text-none :button :minus-circled :crosshair-1 :align-baseline :layout :arrow-down :pencil-2 :link-break-1 :bar-chart :question-mark-circled :checkbox :mixer-horizontal :disc :check :lock-open-2 :grid :symbol :columns :arrow-top-left :paper-plane :drag-handle-vertical :bookmark-filled :triangle-right :cross-circled :value :section :git-hub-logo :row-spacing :speaker-moderate :file :box :external-link :circle :width :link-none-1 :component-none :move :box-model :heart-filled :chat-bubble :component-boolean :dash :dashboard :magnifying-glass :text-align-left :update :lap-timer :minus :vercel-logo :pin-left :star :all-sides :dot-filled :hand :border-all :chevron-up :stopwatch :angle :caret-sort :crumpled-paper :sewing-pin-filled :border-left :divider-horizontal :font-bold :dimensions :underline :id-card :size :hamburger-menu :loop :enter :letter-case-toggle :double-arrow-down :blending-mode :gear :sketch-logo :rows :link-1 :link-break-2 :corners :card-stack-plus :file-plus :drag-handle-dots-1 :mask-off :transparency-grid :stop :eye-closed :border-width :hobby-knife :component-2 :calendar :view-horizontal :activity-log :twitter-logo :icon-jar-logo :code-sandbox-logo :speaker-quiet :speaker-off :file-minus :zoom-out :cookie :thick-arrow-down :face :quote :instagram-logo :countdown-timer :track-next :mixer-vertical :clipboard :half-1 :opacity :strikethrough :envelope-open :crosshair-2 :card-stack-minus :arrow-bottom-left :laptop :eye-none :commit :envelope-closed :clipboard-copy :square :padding :zoom-in :border-none :reader :avatar :plus :container :desktop :pin-top :bell :sun :lightning-bolt :code :drawing-pin-filled :stack :scissors :exit-full-screen :cross-1 :drag-handle-dots-2 :stretch-vertically :camera :align-center-vertically :magic-wand :border-split :pilcrow :letter-case-uppercase :reload :text-align-center :dots-horizontal :layers :image :pause :tokens :cube :cursor-arrow :align-right :border-dotted :shadow-outer :clock :backpack :letter-spacing :eye-open :arrow-bottom-right :component-1 :view-none :caret-down :text-align-justify :text-align-middle :input :dot :target :sewing-pin :ruler-square :radiobutton :play :mobile :rocket :pencil-1 :component-instance :caret-left :letter-case-capitalize :counter-clockwise-clock :open-in-new-window :archive :shadow :eraser :crop :border-top :caret-right :resume :thick-arrow-up :badge :text-align-top :divider-vertical :caret-up :figma-logo :link-none-2 :pin-right :mix :download :lock-closed :value-none :align-top :plus-circled :globe :corner-bottom-left :align-center-horizontally :shadow-none :align-bottom :corner-bottom-right :trash :info-circled :upload :track-previous :overline :switch :check-circled :column-spacing :font-family :cross-2 :stretch-horizontally :dropdown-menu :height :space-evenly-horizontally :border-bottom :heading :star-filled :slider :link-2 :color-wheel :text :card-stack :share-2 :margin})

(register! :ui/icon (into [:enum]
                          (sort (concat feather-icon-names
                                        local-icon-names
                                        react-icon-names))))

(register! :ui/prompt [:map
                       [:ui/prompt :string+]
                       [:ui/icon {:optional true} [:or :ui/icon [:= false]]]
                       [:ui/pred {:optional true} [:or :keyword :rec/fn]]])

(register! :ui/todos [:map
                      [:ui/todos [:vector :string+]]])

(register! :ui/table [:map
                      [:ui/table [:vector [:or :keyword :string+]]]])

(register! :ui/progress-bar [:map
                             [:ui/label [:or :keyword :string+]]
                             [:ui/progress-bar [:map
                                                [:total {:optional true} :int]
                                                [:current {:optional true} :int]]]])

(register! :ui/tabs [:map
                     [:ui/state-key :keyword]
                     [:ui/tabs [:vector [:map]]]])

(register! :ui/header [:map
                       [:ui/header [:enum :fahrzeug]]])

(register! :ui/view [:map])

(register! :ui/field
           (let [types #{:ui/view :ui/prompt :ui/todos :ui/table :ui/progress-bar :ui/tabs :ui/header}]
             (into
              [:multi {:dispatch (comp (partial some types) keys)}
               [nil :ui/attr-field]]
              (map (comp vec (juxt identity identity))) types)))

(register! :ui/message [:map
                        [:message/title [:or :keyword :string+]]
                        [:message/details {:optional true} :string+]

                        [:message.action/route {:optional true} [:vector :ident]]
                        [:message.action/data-testid {:optional true} :string+]

                        [:message/kind [:enum :message.kind/toast]]])

(register! :ui/ref-field (into ui-field-schema
                               [[:ui/lookup-key :keyword]
                                [:ui/lookup-value-type :keyword]]))

; TODO Form schema is only defined here for documentation purposes, it's not used for validation yet because forms are currently generated in a lambda handler and not at compile time, where validation would be useful
(def ui-form-schema
  [:map
   ; Used as state key in deframe
   [:form/name :ident]

   [:ui/fields [:vector :ui/field]]

   ; Displayed in form header as "form title > breadcrumb name"
   [:ui/form-title [:or :keyword :string+]]

   ; Defaults to "New" or "Edit" when form map contains `:ductile/id`
   [:ui/breadcrumb-name {:optional true} :string+]

   ; Link for form-title (the leftmost breadcrumb, e.g to go back from edit form to overview page)
   [:ui/parent-route {:optional true} [:vector :ident]]

   ; Lambda symbol or rec/fn, called with recorded attrs (optionally as transformed by :form/transform-fn)
   [:form/handler {:optional true} [:or :symbol :rec/fn]]
   [:form/on-submit {:optional true} :symbol]

   ;; Lambda returning a file through :ductile.lambda/http
   [:form/download-handler {:optional true} :symbol]

   ; Transform recorded-attrs before submitting (i.e. massage into shape the lambda handler expects)
   [:form/transform-fn {:optional true} :rec/fn]

   ; TODO replace with effect?
   [:form/submit-result-path {:optional true} [:vector :any]]

   ; Flash message displayed above first field
   [:ui/info {:optional true} [:map [:info/message :string+]]]

   ; When set, scrolls form to the specific field with the given :ui/attr (on first render)
   [:form/focus-attr {:optional true} :keyword]

   [:ui/submit-button-label {:optional true} :string+]
   [:ui/submit-button-loading-label {:optional true} :string+]


   ; TODO keep only success-message
   [:ui/success-message {:optional true} :ui/message]
   [:ui/on-create-success {:optional true} :ui/message]
   [:ui/on-edit-success {:optional true} :ui/message]

   ; Whether to serialize recorded form attrs+values to URL query string on submit (for easier refreshing or sharing)
   [:form/set-query-params? {:optional true} :boolean]

   ; Route refetch after submission
   ; TODO DEPRECATED replace usage with on-success effect
   [:form/refetch-on-submit {:optional true} :boolean]])
(register! :ui/form ui-form-schema)

(def ui-bulk-form-schema
  (into ui-form-schema
        [; The payloads to be edited (e.g. fat-fahrzeuge)
         [:form/bulk-rows [:vector :map]]

         ; How each bulk row is identified (probably :ductile/id)
         ; (Bulk form submission is a map of bulk-key to recorded-attrs)
         [:form/bulk-key {:optional true} :keyword]

         ; Human-readable identifying key for each bulk row (e.g. :vehicle/vin)
         ; When editing one row, the value of the bulk-label-key is displayed as the edit modal title
         [:form/bulk-label-key :keyword]

         ; Columns to show in bulk table. Defaults to and merged onto each of :ui/fields.
         ; May override some field properties to render differently in columns (e.g. more compact)
         [:form/bulk-cols {:optional true} [:vector (into ui-field-schema
                                                          ; Whether to hide col when its value in every bulk row is nil
                                                          [[:ui/hide-empty-col? {:optional true} :boolean]])]]]))
(register! :ui/bulk-form ui-bulk-form-schema)

(def billing-customer-reference-schema
  [:map {:closed true :editor/view :location}
   [:ductile/id {:editor/hidden? true} :uuid]
   [:address/description :string+]])

(def tax-rates
  [{:billing.tax-rate/description "Mehrwertsteuer 19%"
    :billing.tax-rate/rate 0.19M
    :billing.tax-rate/tax-code "819"
    :billing.tax-rate/tax-code-pre-system "M19"
    :ductile/id #uuid "02deb0ef-8ff2-42e4-b6f0-a4613be90410"}
   {:billing.tax-rate/description "Mehrwertsteuer 19%" ;; Vorsteuer für Unterehmergutschriften
    :billing.tax-rate/rate 0.19M
    :billing.tax-rate/tax-code "419"
    :ductile/id #uuid "17183788-03ee-4285-b030-8a0f5bee2edf"}
   {:billing.tax-rate/description "Mehrwertsteuer 0%"
    :billing.tax-rate/note "Reverse Charge"
    :billing.tax-rate/tax-code "600"
    :ductile/id #uuid "c3456dbc-c800-49ab-8d64-5663c8b4fb39"}
   {:billing.tax-rate/description "Mehrwertsteuer 0%" ;; Umsatztzeuerbefreiung für Unternehmergutschriften zu ARS Tochterunternehmen
    :billing.tax-rate/note "Intercompany - umsatzsteuerliche Organschaft"
    :billing.tax-rate/tax-code "499"
    :ductile/id #uuid "a887c7cd-dbf1-46c0-8b93-af05ff2d4cb4"}
   {:billing.tax-rate/description "Mehrwertsteuer 0%" ;; Durchreichposten z.B. TÜV Gebühren
    :billing.tax-rate/tax-code "800"
    :ductile/id #uuid"3bae5a6f-1e76-4cec-bab3-f065a25f5416"}])

(defn tax-rate-label [{:billing.tax-rate/keys [description note tax-code]}]
  (cond-> description
    note (str " (" note ")")
    tax-code (str " [" tax-code "]")))

(def tax-rate-schema
  [:map {:closed true
         :editor/view :enum
         :editor/options tax-rates
         :label-key tax-rate-label}
   [:ductile/id :uuid]
   [:billing.tax-rate/rate {:optional true} :bigdec]
   [:billing.tax-rate/tax-code {:optional true} :string+]
   [:billing.tax-rate/tax-code-pre-system {:optional true} :string+]
   [:billing.tax-rate/description {:optional true} :string+]
   [:billing.tax-rate/note {:optional true} :string+]])

(def dealer-location-filter-schema
  [:map-of {:label (tr "Any of the following locations") :editor/view :locations} :uuid :string+])

;; TODO: Generated from workflow/all-workflows via

(comment
  (->> (set/union
        (->> ductile.service.code/codes vals (apply concat) set)
        (->> (ductile.workflow.billing/all-contracts)
             (mapcat (fn [t] (concat (:billing-template/services t)
                                     (:billing-template/accessory-services t))))
             (mapcat (juxt :service/code :service/invoiced-by))
             (remove nil?) set)
        (->> (ductile.workflow/all-contracts)
             (tree-seq (some-fn coll? :contract/services :contract/extra-services :service/services)
                       (some-fn #(when (not (map? %)) (seq %))
                                (fn [n] (concat (:contract/services n)
                                                (:service/services n)
                                                (:contract/extra-services n)))))
             (keep :service/code) set)
        ;; TODO Future workflow services for PIA
        #{"Entfernung Rückstände"
          "Entfernung Transpack"
          "Scheibenfrostschutzmittel"
          "Einlegen SOS-Paket"
          "Handling Laden"})
       sort vec))

(def service-codes
  ["12V battery charge"
   "12V battery check"
   "2. Bereitstellung Gutachter inkl. Wäsche"
   "2. Bereitstellung für Räderwechsel"
   "ARS-ID"
   "Abbruch Eingangsprozess"
   "Abfrage Fahrzeugschein"
   "Abmeldung Fahrzeug"
   "Abort entry process"
   "Accensione e riscaldamento"
   "Ad Blue"
   "Aggiornamento Call-off"
   "Aktivierung Key Card"
   "Aktivierung Servicecard"
   "Aktualisierter Lagerabruf"
   "All-Risk Versicherung"
   "Anbringung Aufkleber"
   "Ankunftsscan"
   "Apertura forzata portiere bloccate"
   "Approval for external workshop"
   "Approvazione per lavorazione esterna"
   "Arrival scan"
   "Assign Contract"
   "Aufwand manuelle Erfassung"
   "Ausfüllen Übergabeprotokoll im Beisein des Abholers"
   "Ausrüsten"
   "Außenwäsche & Finish"
   "Aviloo Test"
   "Avise Räderwechsel"
   "Avise Trackerverbau"
   "Avviamento Booster"
   "Bahnbeladung"
   "Bahnentladung"
   "Batterie 12V laden"
   "Batterie 12V messen"
   "Batterie 12V prüfen"
   "Batterie 12V prüfen (Eingang)"
   "Batterie 12V wechseln"
   "Batterie prüfen"
   "Batterie prüfen Stockpflege"
   "Batteriepflege 6W"
   "Begleitung bei Besichtigung"
   "Begutachtung"
   "Bereitstellung Abholung Auf Achse"
   "Bereitstellung Fremddienstleister"
   "Bereitstellung Fremdfirmen"
   "Bereitstellung Gutachter"
   "Bereitstellung Gutachter inkl. Wäsche"
   "Bereitstellung Selbstabholer"
   "Bereitstellung Transport"
   "Bereitstellung für Aufkäufer"
   "Bereitstellung verpackter Radsatz zur Abholung"
   "Bestandsmeldung"
   "Bewertung Re-Lease"
   "Bodensäuberung"
   "Booster - starten"
   "Bremsscheiben freibremsen"
   "Call-off"
   "Call-off update"
   "Caricamento allegato"
   "Caricamento batteria"
   "Caricamento batteria HV"
   "Check Wärmebildkamera"
   "Ciclo perizie DEKRA"
   "Classification"
   "Classificazione"
   "Classificazione pneumatici"
   "Clear from loading area"
   "Collegamento batteria 12V"
   "Conferma FPR"
   "Conferma Ingresso"
   "Confirm entry"
   "Contract Assignment"
   "Controllo Accessori"
   "Controllo Batteria 12V"
   "Controllo Batteria HV"
   "Controllo VIN"
   "Controllo batteria HV"
   "Controllo clima"
   "Correzione VIN"
   "Correzione dati"
   "Correzione ingresso"
   "Data correction"
   "Dateianhang hochladen"
   "Datenkorrektur"
   "Demaskierung"
   "Demontage Kennzeichen"
   "Einbau Telematiksystem Active Link"
   "Eingang bestätigen"
   "Eingangsinspektion"
   "Eingangskontrolle"
   "Eingangskorrektur"
   "Eingangsmeldung"
   "Eingangsprotokoll"
   "Einlagerung Rädersatz für max. 6 Monate"
   "Einlegearbeiten"
   "Einlegen Dokumente Nord"
   "Einlegen Dokumente Süd"
   "Einlegen SOS-Paket"
   "Einlegen Versicherungskarte / Fahrzeugschein(kopie)"
   "Entfernung Rückstände"
   "Entfernung Transpack"
   "Entkleben/Entfolieren"
   "Entnahme 2. Radsatz"
   "Entnahme Fahrzeugschein"
   "Entnahme Losteile"
   "Entnahme Losteile / Schlüsselanhänger anbringen"
   "Entnahme Zweiter Radsatz"
   "Entry correction"
   "Entry inspection"
   "Erfassung Transportdaten"
   "FIAT Schadensdokumentation"
   "FIN Kontrolle"
   "FIN-Korrektur"
   "FPR Inspektion"
   "FPR entry"
   "FPR inspection"
   "FPR-Eingang"
   "FZ-Außenreinigung"
   "FZG-Bereitstellung Selbstabholer"
   "Fahrzeug Bereitstellung"
   "Fahrzeug bewegen"
   "Fahrzeugavise"
   "Fahrzeuggrößenklassifizierung"
   "Fahrzeugwäsche"
   "Fahrzeugwäsche +Bereitstell Gutacht"
   "Fahrzeugwäsche wegen Saharastaub"
   "Feinstaubplakette ankleben"
   "Finish Vorführwagen"
   "Finish innen und außen"
   "Fotodokumentation demontierter Radsatz"
   "Fotodokumentation verpackter Radsatz"
   "Fotografia danni"
   "Fotos anfertigen, Paket groß"
   "Fotos anfertigen, digital max.5 Stk"
   "Fotos erfassen"
   "Freigabe für externe Werkstatt"
   "Freigabe zur Abholung"
   "Fremdstarten"
   "Fueling"
   "Fz-Aufbereitung inkl. Polieren"
   "Geschwindigkeitsabriegelung"
   "Gestione"
   "Gutachterzuordnung"
   "HV battery charge"
   "HV battery check"
   "Handling Batterie laden"
   "Handling Laden"
   "Handling get-mobile package"
   "Handling, Versand Losteile"
   "Hochvoltbatterie laden"
   "Hochvoltbatterie laden (nach Abruf)"
   "Hochvoltbatterie prüfen"
   "Hochvoltbatterie prüfen (Eingang)"
   "Import announced vehicle"
   "Importazione vettura"
   "Ingresso FPR"
   "Innenraumreinigung"
   "Inserimento Kit / Accessori"
   "Installazione BB + Appaiamento"
   "Interruzione ingresso"
   "Ispezione Danni"
   "Ispezione Danni FPR"
   "Ispezione FPR"
   "Ispezione all'ingresso"
   "Ispezione carburante"
   "Ispezione esterno"
   "Ispezione interno"
   "Ispezione parti in gomma"
   "Ispezione sotto cofano"
   "Ispezione tergicristallo"
   "Kennzeichen Versand"
   "Kennzeicheneingang bestätigen"
   "Klassifizierung"
   "Kleines Finish inkl. Fahrzeugwäsche"
   "Kunde begleiten"
   "LIDL Paket 1 Standard"
   "Ladebereich freigeben"
   "Lager"
   "Lagerabruf"
   "Lagerpflege"
   "Lavaggio"
   "Lavaggio PDI"
   "Manuelle Tätigkeit"
   "Mitarbeiter Privatverkauf Vorführung (30 min. Begleitung durch ARS)"
   "Montage Kennzeichen"
   "Montage Kennzeichen / Umweltplakete"
   "Montage Kennzeichen / Umweltplakette"
   "Montage Kennzeichenhalter"
   "Montage Kennzeichenhalter \"Arval\""
   "Montaggio targa"
   "Motorhaube öffnen"
   "Movimentazione con carrello elevatore"
   "Movimentazione extra"
   "NW Finish innen und außen"
   "Nachkontrolle Losteile"
   "Nachprüfung"
   "Optimo-Eingangs-Check"
   "Ozonbehandlung"
   "PDI"
   "PDI - Controllo Batteria 12V"
   "PDI - Controllo Batteria HV"
   "PDI - Inserimento accessori"
   "PDI - Inserimento libretti"
   "PDI - Inserimento tappetini"
   "Paket Eingangskontrolle + Protokoll"
   "Paket Fahrzeughandling Neuwagen"
   "Paket LEAN"
   "Paket Rückläufermanagement"
   "Preparazione AUTOVIA"
   "Preparazione Autosystem"
   "Preparazione Autotorino"
   "Preparazione Autozzatti c/o Drivalia"
   "Preparazione Ceccato"
   "Preparazione Centauro"
   "Preparazione Drivalia"
   "Preparazione EUROPCAR"
   "Preparazione LOCAUTO"
   "Preparazione Noleggiare"
   "Preparazione Record Go"
   "Preparazione Securiti Fleet"
   "Preparazione per il trasporto"
   "Prepare for transport"
   "Pressione pneumatici"
   "Prüfaufwand"
   "Prüfung Kühlschlauch"
   "Quick turnaround"
   "Radsatz Kontrolle"
   "Radsatz anfordern"
   "Radsatz bereitstellen"
   "Radsatz einlagern"
   "Radsatz in Empfang nehmen"
   "Reaktivierung stromloses FZ"
   "Record transport data"
   "Registrazione dati trasporto"
   "Reifen einlegen (mit Sicherung)"
   "Reifen einlegen (ohne Sicherung)"
   "Reifen umziehen"
   "Reifen umziehen inkl. Wuchten"
   "Reifen umziehen inkl. Wuchten, Gewichte und Ventil"
   "Reifendruck anpassen"
   "Reifendruck prüfen"
   "Reifendruck prüfen ggf. anpassen"
   "Remove from load"
   "Reparking"
   "Rifornimento"
   "Rimozione Gancio Traino"
   "Rimozione da area di carico"
   "Rimozione dal carico"
   "Rimozione pellicole"
   "Rimozione pellicole cofano"
   "Rimozione pellicole large"
   "Rimozione pellicole small"
   "Rimozione ruggine"
   "Rimozione targa"
   "Riposizionamento"
   "Rotazione pneumatici"
   "Räder einlegen (mit Sicherung)"
   "Räder einlegen inkl. Zurrgurte"
   "Räder verpacken"
   "Räder zum Versand verpacken"
   "Räderhandling inkl. Zwischenlagerung"
   "Rädermanagement"
   "Räderwechsel"
   "Räderwechsel (Satz)"
   "Räderwechsel abbrechen"
   "Rücklagerung Radsatz"
   "Rücknahme Gebrauchte"
   "Rücknahmeprotokoll"
   "Saugen"
   "Scansione iniziale"
   "Scarico treno"
   "Scheiben abkleben"
   "Scheibenfrostschutzmittel"
   "Schlüsselbatterie ersetzen"
   "Schlüsselhandling"
   "Schnelldreher verbringen"
   "Scollegamento batteria 12V"
   "Scollegamento tampone"
   "Sdoganamento H1"
   "Self Pickup"
   "Self-pickup"
   "Sostituzione batteria"
   "Spedizione materiale"
   "Spostamento in Piazzale"
   "Startdatum Lagerpflege ermitteln"
   "Storage"
   "Storno bereitgestelltes Fahrzeug"
   "Stundensatz Reifenhandling"
   "Tag Ausbau"
   "Tag Einbau"
   "Tagliando"
   "Tanken"
   "Tankhandling"
   "Tire pressure adjustment"
   "Tracker Ausbau"
   "Tracker Einbau"
   "Train unloading"
   "Transito rapido"
   "Transportschadenabwicklung"
   "TÜV-Gebühren HU/AU"
   "UVV-Prüfung"
   "Umfuhr Arbeitsplatz"
   "Umfuhr Lagerplatz"
   "Umparken"
   "Upload Attachment"
   "VIN check"
   "VIN correction"
   "Verkauf an Arval InFleet"
   "Vermietvorbereitung"
   "Verrechnungs-Satz Sonderarbeiten"
   "Verschmutzung erfassen"
   "Versenden Kennzeichen & Zulassung"
   "Vertragsumbuchung"
   "Vertragszuweisung"
   "Vom Ausgangstransport entfernen"
   "Vorführung HU/AU"
   "Vorwäsche"
   "Waschen"
   "Wegrollen absichern"
   "Werkstatt"
   "Werkstatthandling HU/AU"
   "Werkstatthandling extern Abholung"
   "Werkstatthandling extern Verbringung"
   "Wochenpflege"
   "Wäsche"
   "Zubehör laut Übergabeprotokoll abgeglichen"
   "Zugentladung"
   "Zustandserfassung"
   "Zwangsöffnen"
   "Übergabeinspektion"])


(def price-pred-symbols
  '[bca-transporter?
    bca-van?
    pia-large-cabin?
    charging-eitting-compound?
    completed-after-pdi?
    completed-before-pdi?
    destination-weller-gütersloh?
    engine-type-combustion?
    engine-type-hybrid?
    engine-type-electric-or-hybrid?
    entry-wheel-set?
    fuel-mini-100-percent-for-filiale-nord?
    fuel-mini-100-percent-for-filiale-süd?
    fuel-mini-100-percent?
    included-in-arval-infleet-entry-package?
    kia-model-has-one-lashing-hook?
    kia-model-has-two-lashing-hooks?
    light-commercial-vehicle?
    loading-with-forklift?
    not-aldi?
    omoda-model-has-one-lashing-hook?
    omoda-model-has-two-lashing-hooks?
    open-parking-not-quarantined?
    performed-on-public-holiday?
    performed-on-saturday?
    performed-on-sunday?
    permanently-quarantined?
    service-credited-on-compound?
    transported-by-ps-team?
    scheduled-service?])

(comment
  #_(->> (ductile.contract/load-contracts (dev/db))
         (mapcat :contract/billing-templates)
         (tree-seq coll? identity)
         (filter :billing/preds)
         (mapcat :billing/preds)
         (map (comp symbol name))
         set
         sort
         vec))




(def postprocess-fn-symbols
  '[postprocess-battery-charge-handling
    postprocess-batterie-prüfen-for-batterie-laden
    postprocess-bereitstellung-eli
    postprocess-bereitstellung-sebstabholer
    postprocess-bereitstellung-sebstabholer-when-not-transported-by-ars
    postprocess-bereitstellung-selbstabholer-aldi
    postprocess-kia-pdi
    postprocess-paket-fahrzeughandling-neuwagen
    postprocess-paket-rückläufermanagement
    postprocess-port-handling-when-moved-to-yard
    postprocess-recurring-all-risk-versicherung
    postprocess-recurring-weekly-storage-care
    postprocess-service-aufwand-manuelle-erfassung
    postprocess-service-eingangsmeldung
    postprocess-service-bestandsmeldung
    postprocess-service-bahnentladung
    postprocess-service-get-mobile-package
    postprocess-service-lidl-packet-1-standard
    postprocess-service-rücknahme-gebrauchte
    postprocess-storage-compound-entry-exit
    postprocess-storage-compound-entry-exit-when-call-off-required
    postprocess-storage-for-lean-package-only
    postprocess-storage-for-open-parking-only
    postprocess-storage-until-active-custody
    postprocess-tüv-gebühren
    postprocess-wäsche-for-finish])

(comment
  #_(->> (ductile.contract/load-contracts (dev/db))
         (mapcat :contract/billing-templates)
         (mapcat :billing-template/postprocess)
         (map (comp symbol name))
         set
         sort
         vec))

(def contract-names
  ["AHAG Gelsenkirchen"
   "Aktion StarCar"
   "Arval DeFleet"
   "Arval InFleet"
   "Arval InFleet Großkunde BMW"
   "Automag"
   "BCA"
   "BMW"
   "BMW AVR Räderwechsel"
   "BMW Aldi"
   "BMW Lager Werke"
   "BMW München"
   "BMW Niederlassungen"
   "BMW Rückholer"
   "BMW Unfall"
   "BMW Verteilung"
   "Bobrink"
   "Boomers"
   "CarOnSale Verteilung"
   "Cargentur"
   "ELOG Routing"
   "Ernst"
   "Europcar Infleet"
   "Faba"
   "Kaltenbach"
   "Kia Auto Grifone"
   "Kia Autolidotre"
   "Kia Autosystem"
   "Kia Autotorino"
   "Kia Autovia"
   "Kia Centauro"
   "Kia Clerici"
   "Kia Distribuzione"
   "Kia Europcar"
   "Kia Gecar"
   "Kia Iperauto"
   "Kia Leonori"
   "Kia Locauto"
   "Kia Noleggiare"
   "Kia Nordauto"
   "Kia Reali"
   "Kia Record Go"
   "Kia Securiti Fleet"
   "Kohl"
   "LEAN Timmermanns"
   "LIDL Gebrauchtwagen"
   "LIDL Neuwagen"
   "OPENLANE Gebrauchtwagen"
   "Omoda & Jaecoo Distribuzione"
   "Philipp"
   "Procar"
   "Procar Sonderauftrag"
   "SIXT"
   "Schmidt"
   "Sixt Infleet"
   "Spaett"
   "TNR Mini"
   "Timmermanns"
   "Tölke & Fischer"
   "Unternehmergutschriften"
   "VGI Usato Audi GWS Distribution"
   "VGI Usato Distribution"
   "Weller Premium"])

(comment
  #_(->> (ductile.datomic.api/q '[:find [?cn ...] :where [_ :contract/name ?cn]] (dev/db))
         sort
         vec))

(def extra-line-item-types
  [:line-item.type/storage-flat-rate
   :line-item.type/accessory-storage
   :line-item.type/entry-report-flat-rate])

(def line-item-types
  (vec (concat extra-line-item-types
               [:line-item.type/service
                :line-item.type/accessory-service
                :line-item.type/transport
                :line-item.type/load
                :line-item.type/storage])))

(def extra-line-item-type-enum
  (into [:enum] extra-line-item-types))

(defn line-item-filter-complement-schema [filter-schema]
  [:or
   filter-schema
   [:tuple {:editor/always-visible-label (tr "All except the following")} [:= {:label ""} :complement] filter-schema]])

(defn set-of-or-complement [set-schema]
  (line-item-filter-complement-schema [:set {:label (tr "Only the following")} set-schema]))

(def accounting-types
  [:accounting.type/cost
   :accounting.type/revenue])

(def line-item-selection
  [:map {:closed true}
   [:line-item/type {:optional true} (set-of-or-complement (into [:enum] line-item-types))]
   [:fahrzeug/destination {:optional true} (line-item-filter-complement-schema dealer-location-filter-schema)]
   [:fahrzeug/call-off-destination.id {:optional true} (line-item-filter-complement-schema dealer-location-filter-schema)]
   [:service/manually-ordered? {:optional true} boolean?]
   [:destination/location.id {:optional true} (line-item-filter-complement-schema dealer-location-filter-schema)]
   [:transport-order/forwarder.id {:optional true} (line-item-filter-complement-schema dealer-location-filter-schema)]
   [:origin/location.id {:optional true} (line-item-filter-complement-schema dealer-location-filter-schema)]
   [:service/code {:optional true} (set-of-or-complement (into [:enum] service-codes))]
   [:billing/description {:optional true} (set-of-or-complement :string+)]
   [:compound/name {:optional true} (set-of-or-complement :compound/name)]
   [:transport/name {:optional true} (set-of-or-complement :string+)]
   [:contract/name {:optional true} (set-of-or-complement (into [:enum] contract-names))]
   [:make/name {:optional true} (set-of-or-complement :string+)]
   [:accounting/type {:optional true} (set-of-or-complement (into [:enum] accounting-types))]])

(register! :bigdec #?(:clj decimal?
                      :cljs :double))

(register! :billing/price :bigdec)
(register! :billing/amount :int)

(def billing-service-codes
  (into [:enum] service-codes))

(def fixed-price-service
  [:map {:closed true
         :label (tr "Fixed price service")
         :label-key [:billing/description :service/code]
         :description (tr "always invoiced at this price")}
   [:billing/description {:optional true} :string+]
   [:service/code billing-service-codes]
   [:billing/price :billing/price]
   [:billing/cost {:optional true} :billing/price]
   [:billing/package-includes {:optional true} [:set billing-service-codes]]
   [:customer/service-code {:optional true} :string+]
   [:contract-terms/notes {:optional true} :string]])

(def unit-price-service
  [:map {:closed true :label (tr "Unit price service") :description (tr "e.g. Ad Blue, Manual work") :label-key [:billing/description :service/code]}
   [:billing/description {:optional true} :string+]
   [:service/code billing-service-codes]
   [:billing/unit-price :billing/price]
   [:billing/unit [:enum :unit/hour :unit/liter :unit/minute]]
   [:billing/work-unit {:optional true} [:int {:min 1}]]
   [:billing/package-includes {:optional true} [:set billing-service-codes]]
   [:customer/service-code {:optional true} :string+]
   [:contract-terms/notes {:optional true} :string]])

(def invoiced-by-other-service
  [:map {:closed true :label (tr "Invoiced by other service") :label-key [:billing/description :service/code]}
   [:service/code billing-service-codes]
   [:service/invoiced-by (into [:enum] service-codes)]
   [:billing/price {:optional true} zero?]
   [:contract-terms/notes {:optional true} :string]])

(def conditional-prices
  [:and
   [:cat
    [:+ [:map {:label-key :billing/description}
         [:billing/description
          {:optional true
           :description (tr "shown on the invoice in parentheses after the main invoice text. Example: Battery charging (Hybrid)")
           :label (tr "additional invoice text")}
          :string+]
         [:billing/price :billing/price]
         [:billing/preds [:vector [:or :rec/fn (into [:enum {:label (tr "Select existing price predicate")}] price-pred-symbols)]]]]]
    [:? {:label (tr "Optional standard price")
         :description (tr "If no other condition is met, this price will be used. If the price is not set, the invoice will show an error. Example: If multiple conditional prices are set based on the vehicle type but no vehicle type is saved, there will be no price used unless there is an optional standard price and the invoice will show an error.")}
     [:or
      [:billing/price {:label (tr "Standard price")}]
      [:nil {:label (tr "not set")}]]]]
   vector?])

(def conditional-price-service
  [:map {:closed true
         :label (tr "Conditional price service")
         :label-key [:billing/description :service/code]
         :description (tr "can have different prices and invoice texts depending on the condition")}
   [:billing/description {:optional true} :string+]
   [:service/code billing-service-codes]
   [:customer/service-code {:optional true} :string+]
   [:billing/prices {:optional true} conditional-prices]
   [:billing/costs {:optional true} conditional-prices]
   [:contract-terms/notes {:optional true} :string]])

(def conditional-costs-service
  [:map {:closed true :label (tr "Service with conditional costs only") :label-key [:billing/description :service/code]}
   [:billing/description {:optional true} :string+]
   [:service/code billing-service-codes]
   [:customer/service-code {:optional true} :string+]
   [:billing/costs conditional-prices]
   [:contract-terms/notes {:optional true} :string]])

(def cost-only-service
  [:map {:closed true :label (tr "Service with costs only") :label-key [:billing/description :service/code]}
   [:billing/description {:optional true} :string+]
   [:service/code billing-service-codes]
   [:billing/cost :billing/price]
   [:customer/service-code {:optional true} :string+]
   [:contract-terms/notes {:optional true} :string]])

(def fixed-price-conditional-costs-service
  [:map {:closed true :label (tr "Fixed price service but conditional costs") :label-key [:billing/description :service/code]}
   [:billing/description {:optional true} :string+]
   [:service/code billing-service-codes]
   [:billing/price :billing/price]
   [:billing/costs conditional-prices]
   [:billing/package-includes {:optional true} [:set billing-service-codes]]
   [:customer/service-code {:optional true} :string+]
   [:contract-terms/notes {:optional true} :string]])

(def service-pricing
  [:or {:label-key [:billing/description :service/code]}
   fixed-price-service
   conditional-price-service
   unit-price-service
   invoiced-by-other-service
   fixed-price-conditional-costs-service
   conditional-costs-service
   cost-only-service])

(comment
  (first (:errors (m/explain (m/schema [:map [:billing-template/services [:vector service-pricing]]])
                             {:billing-template/services
                              [{:billing/prices [{:billing/preds '[destination-weller-gütersloh?] :billing/price 10.60M} 8.50M]
                                :service/code "Tanken"}]}))))

(def billing-feature-schema
  [:enum
   :feature/line-item-selection
   :feature/line-item-partition])

(def invoice-column-headers
  ["Accounting Month"
   "Accounting Year"
   "Active Custody"
   "Amount "
   "Calculated from"
   "Calculated until"
   "Charged"
   "City"
   "Completed at"
   "Completion Weekday"
   "Compound"
   "Costs"
   "Customer Load Number"
   "DDT Number"
   "Date"
   "Days"
   "Destination City"
   "Destination Location"
   "Dispatcher"
   "Distance"
   "Entry"
   "Entry Mode"
   "Entry Transport Name"
   "Entry Weekday"
   "Exit"
   "Flatrate"
   "Handling"
   "Handling Costs"
   "Height (mm)"
   "Included"
   "Invoice Number"
   "LCV"
   "Length (mm)"
   "License plate"
   "Load ID"
   "Location"
   "Make"
   "Model"
   "Origin City"
   "Origin Location"
   "Postcode"
   "Price/Day"
   "Province Code"
   "Service"
   "Status"
   "Storage"
   "Storage Costs"
   "Street"
   "Transport Date"
   "Transport Name"
   "Unit Price Description"
   "VIN"
   "VINs"
   "Van"
   "Vehicle Count"
   "Wheel-set Type"
   "Width (mm)"])

(comment
  #_(vec (sort (set (concat
                     ["Invoice Number" "License plate" "Postcode" "Street" "Customer Load Number" "Wheel-set Type"
                      "Entry Transport Name" "Active Custody" "Entry Weekday" "Entry Mode" "Accounting Month"
                      "Accounting Year" "Price/Day" "LCV" "Van" "Dispatcher"]
                     ductile.billing/load-invoice-columns
                     ductile.billing/storage-invoice-columns
                     ductile.billing/flatrate-invoice-columns
                     ductile.billing/transport-invoice-columns
                     ductile.billing/service-invoice-columns
                     ductile.billing/storage-by-day-invoice-columns
                     ductile.billing/accessory-storage-invoice-columns)))))

(def billing-header-schema
  (m/schema [:map {:closed true}
             [:billing.type/accessory-storage {:optional true} [:sequential (into [:enum] invoice-column-headers)]]
             [:billing.type/storage {:optional true} [:sequential (into [:enum] invoice-column-headers)]]
             [:billing.type/loads {:optional true} [:sequential (into [:enum] invoice-column-headers)]]
             [:billing.type/services {:optional true} [:sequential (into [:enum] invoice-column-headers)]]
             [:billing.type/accessory-services {:optional true} [:sequential (into [:enum] invoice-column-headers)]]
             [:billing.type/transports {:optional true} [:sequential (into [:enum] invoice-column-headers)]]]))

(def email-config-schema
  (m/schema [:map {:closed true}
             [:to {:label (tr "Recipients")} [:sequential :string+]]
             [:attachment-types [:sequential [:enum ;; TODO could this be a set of enum?
                                              :email-attachment-type/invoice-pdf
                                              :email-attachment-type/invoice-appendix-pdf
                                              :email-attachment-type/invoice-appendix-excel]]]]))

(def billing-section-types
  [:billing.type/services
   :billing.type/storage
   :billing.type/accessory-storage
   :billing.type/transports])

(def billing-section-type
  (into [:enum] billing-section-types))

(def billing-type-enum
  [:enum
   :billing.type/storage
   :billing.type/accessory-storage
   :billing.type/transports
   :billing.type/services
   :billing.type/flat-rates
   :billing.type/loads
   :billing.type/storage-by-day])

(def line-item-keys-enum
  [:enum
   :compound/name
   :billing/description
   :service/code
   :service/completed-at
   '...])

(def item-order-schema [:map-of billing-type-enum [:vector line-item-keys-enum]])

(def fuel-groups
  ["Aral +0%"
   "Shell +0%"
   "Shell +1%"
   "Shell +2%"
   "Shell +5%"
   "Shell +10%"
   "Shell +15%"
   "Shell +25%"
   "Shell +35%"])

(comment
  #_(->> (ductile.fuel-prices/show-fuel-prices-fields (dev/db) {})
         :fuel-price/groups
         vec))

(def compounds-list-schema
  [:vector (into [:enum] compound-names)])


(def fixed-price-transport
  [:map {:closed true :label (tr "Fixed price transport")}
   [:fixed-price :billing/price]])


(def fixed-prices-transport
  [:map {:closed true :label (tr "fixed price transport with preds")}
   [:fixed-prices conditional-prices]])


(register! :province-code :string+)
(register! :model-code :string+)
(register! :location-code :string+)


(def transport-price-by-location+model
  [:or {:label (tr "Transport price by location and model")}
   [:map {:closed true :label (tr "Price per location code for models + optional by province code")}
    [:location-code-models [:map-of :location-code [:map-of :model-code number?]]]
    [:province-code-models {:optional true} [:map-of :province-code [:map-of :model-code number?]]]]
   [:map {:closed true :label (tr "Price per province code & model")}
    [:province-code-models [:map-of :province-code [:map-of :model-code number?]]]]])


(register! :compound-name-keyword :keyword)

(def price+costs-by-compound+location+model
  [:or {:label (tr "Prices & costs for compounds, location and model")}
   [:map {:closed true :label (tr "Prices & costs for compounds per location & models + optional by province code")}
    [:location-code-models-by-compound [:map-of :compound-name-keyword
                                        [:map-of :location-code
                                         [:map {:closed true}
                                          [:costs [:map-of :model-code number?]]
                                          [:revenues [:map-of :model-code number?]]]]]]
    [:province-code-models-by-compound {:optional true} [:map-of :compound-name-keyword
                                                         [:map-of :province-code
                                                          [:map {:closed true}
                                                           [:costs [:map-of :model-code number?]]
                                                           [:revenues [:map-of :model-code number?]]]]]]]
   [:map {:closed true :label (tr "Prices & costs for compounds per province code & model")}
    [:province-code-models-by-compound [:map-of :compound-name-keyword
                                        [:map-of :province-code
                                         [:map {:closed true}
                                          [:costs [:map-of :model-code number?]]
                                          [:revenues [:map-of :model-code number?]]]]]]]])



(def transport-settings-schema
  [:or fixed-price-transport fixed-prices-transport transport-price-by-location+model price+costs-by-compound+location+model])

(register! :carrier/code :string+)

(def load-settings-schema-by-compound
  [:map {:closed true}
   [:location-prices-by-compound {:optional true} [:map-of :compound-name-keyword
                                                   [:vector [:map #_{:closed true}
                                                             [:billing/amount :billing/amount]
                                                             [:location/ids [:set :uuid]]
                                                             [:ductile/distance {:optional true} [:or :double :nil]]
                                                             [:address/name {:optional true}]
                                                             [:address/street {:optional true}]
                                                             [:address/postcode {:optional true}]
                                                             [:address/city {:optional true}]]]]]


   [:distance-prices-by-compound {:optional true} [:map-of :compound-name-keyword
                                                   [:vector [:map {:closed true}
                                                             [:distance-to :billing/price]
                                                             [:billing/amount :billing/amount]]]]]
   [:postcode-prefix-prices-by-compound {:optional true}
    [:map-of :compound/name
     [:map
      [:short-distance {:optional true} [:vector [:map
                                                  [:address/postcode [:re #"\d{5,5}"]]
                                                  [:address/name {:optional true} :string+]
                                                  [:billing/price :billing/price]]]]
      [:long-distance [:vector [:map
                                [:address/postcode [:re #"\d{1,5}"]]
                                [:address/name {:optional true} :string+]
                                [:compound/name {:optional true} :compound/name]
                                [:billing/price :billing/price]]]]]]]])




(def storage-settings
  [:or
   [:map {:closed true :label (tr "fixed prices & fixed handling price")}
    [:storage/price :billing/price],
    [:storage/handling-price {:optional true} :billing/price],
    [:storage/included-days {:optional true} int?]
    [:included-flat-rate-cars-per-day {:optional true} int?]
    [:contract-terms/notes {:optional true} :string+]]
   [:map {:closed true :label (tr "fixed price & conditional handling price")}
    [:storage/price :billing/price]
    [:storage/cost {:optional true} :billing/price]
    [:storage/handling-prices {:optional true} conditional-prices],
    [:storage/included-days {:optional true} int?]
    [:included-flat-rate-cars-per-day {:optional true} int?]
    [:contract-terms/notes {:optional true} :string+]]
   [:map {:closed true :label (tr "conditional price & fixed handling price")}
    [:storage/prices conditional-prices]
    [:storage/costs {:optional true} conditional-prices]
    [:storage/handling-price {:optional true} :billing/price],
    [:storage/included-days {:optional true} int?]
    [:included-flat-rate-cars-per-day {:optional true} int?]
    [:contract-terms/notes {:optional true} :string+]]
   [:map {:closed true :label (tr "conditional price & conditional handling price")}
    [:storage/prices conditional-prices]
    [:storage/handling-prices {:optional true} conditional-prices]
    [:storage/costs {:optional true} conditional-prices]
    [:storage/handling-costs {:optional true} conditional-prices]
    [:storage/included-days {:optional true} int?]
    [:included-flat-rate-cars-per-day {:optional true} int?]
    [:contract-terms/notes {:optional true} :string+]]])

(def accessory-storage-settings
  [:map {:closed true}
   [:accessory.kind/wheel-set storage-settings]])

(def billing-extra-item-schema
  (m/schema [:map {:closed true}
             [:billing/description :string+]
             [:billing/type billing-type-enum]
             [:line-item/type extra-line-item-type-enum]
             [:line-item/billed-item [:tuple [:= :compound/name] :compound/name]]
             [:billing/preds [:vector [:or :rec/fn (into [:enum {:label (tr "Select existing price predicate")}] price-pred-symbols)]]]
             [:billing/frequency [:enum :months :weeks]]
             [:billing/amount :int]]))

(def terms-document
  (m/schema
   [:map {:closed true :editor/view :cas-file}
    [:ductile/id :uuid]
    [:cas/multihash {:optional true} :cas/multihash]
    [:file/mime-type {:optional true} :string]
    [:file/name {:optional true} :file/name]]))

(def currencies
  [:billing/currency])

(def charge-reconciliation
  [:enum
   :charge.reconciliation/unknown ;; not sure yet how to invoice or what price to charge
   :charge.reconciliation/invoice ;; invoiced to customer
   :charge.reconciliation/package ;; billed by other line item
   :charge.reconciliation/not-invoiced]) ;; should not be invoiced])

(def charge-service-filter
  :map)

(def charge-modes
  [:charge.mode/multiple-charges :charge.mode/unique-charge])

(def service-charge
  [:map {:closed true}
   [:charge/label :string+]
   [:charge/price {:optional true} :billing/price]
   [:charge/reconciliation charge-reconciliation]
   [:charge/label-fn {:optional true} :any]
   [:charge/summary-key-fn {:optional true} :any]
   [:charge/unit {:optional true} [:enum :unit/day :unit/hour :unit/liter]]
   [:charge/price-fn {:optional true} :symbol]
   [:charge/selection {:optional true} [:vector charge-service-filter]]])

(def service-terms-pricing
  [:map {:closed true :label (tr "Service pricing")}
   [:pricing/category [:= :category/service]]
   [:pricing/billable [:vector :any]]
   [:customer/service-code {:optional true} :string+]
   [:workflow/phase {:optional true} [:enum :workflow.phase/after-arrival :workflow.phase/after-entry :workflow.phase/after-call-off :workflow.phase/after-exit]]
   [:workflow/optionality {:optional true} [:enum :optionality/can :optionality/must :optionality/should :optionality/order]]
   [:pricing/charge-mode {:optional true} (into [:enum] charge-modes)]
   [:pricing/charged-by {:optional true} :any]
   [:pricing/charges [:vector service-charge]]])


(def charge-accessory-storage-filter
  :map)

(def accessory-storage-charge
  [:map {:closed true}
   [:charge/label :string+]
   [:charge/price {:optional true} :billing/price]
   [:charge/reconciliation charge-reconciliation]
   [:charge/included-days {:optional true} :int]
   [:charge/unit {:optional true} [:enum :unit/day]]
   [:charge/price-fn {:optional true} :symbol]
   [:charge/summary-key-fn {:optional true} :any]
   [:charge/selection {:optional true} [:vector charge-accessory-storage-filter]]])

(def accessory-storage-pricing
  [:map {:closed true :label (tr "Accessory pricing")}
   [:pricing/category [:= :category/accessory-storage]]
   [:pricing/billable [:vector :any]]
   [:pricing/charges [:vector accessory-storage-charge]]])

(def charge-transport-filter
  :map)

(def transport-charge
  [:map {:closed true}
   [:charge/label :string+]
   [:charge/price {:optional true} :billing/price]
   [:charge/reconciliation charge-reconciliation]
   [:charge/label-fn {:optional true} :any]
   [:charge/price-fn {:optional true} :symbol]
   [:charge/price-data-fn {:optional true} :any]
   [:charge/summary-key-fn {:optional true} :any]
   [:charge/selection {:optional true} [:vector charge-transport-filter]]])

(def transports-terms-pricing
  [:map {:closed true :label (tr "Transport pricing")}
   [:pricing/category [:= :category/transport]]
   [:pricing/aggregation-fn {:optional true} :symbol]
   [:pricing/aggregation-filter-fn {:optional true} :any]
   [:pricing/billable [:vector :any]]
   [:pricing/charge-mode {:optional true} (into [:enum] charge-modes)]
   [:pricing/charges [:vector transport-charge]]])

(def charge-storage-filter
  :map)

(def storage-charge
  [:map {:closed true}
   [:charge/label :string+]
   [:charge/price {:optional true} :billing/price]
   [:charge/reconciliation charge-reconciliation]
   [:charge/included-days {:optional true} :int]
   [:charge/unit {:optional true} [:enum :unit/day]]
   [:charge/summary-key-fn {:optional true} :any]
   [:charge/price-fn {:optional true} :symbol]
   [:charge/selection {:optional true} [:vector charge-storage-filter]]])

(def storage-flat-rate-charge
  [:map {:closed true}
   [:charge/label :string+]
   [:charge/price {:optional true} :billing/price]
   [:charge/reconciliation charge-reconciliation]
   [:charge/included-days {:optional true} :int]
   [:charge/unit {:optional true} [:enum :unit/day]]
   [:charge/included-cars-per-day {:optional true} :int]
   [:charge/summary-key-fn {:optional true} :any]
   [:charge/price-fn {:optional true} :symbol]
   [:charge/selection {:optional true} [:vector charge-storage-filter]]])

(def storage-terms-pricing
  [:map {:closed true :label (tr "Storage pricing")}
   [:pricing/category [:= :category/storage]]
   [:pricing/billable [:vector :any]]
   [:pricing/charge-mode {:optional true} (into [:enum] charge-modes)]
   [:pricing/charges [:vector storage-charge]]])

(def storage-flat-rate-terms-pricing
  [:map {:closed true :label (tr "Storage Flat-Rate pricing")}
   [:pricing/category [:= :category/storage-flat-rate]]
   [:pricing/aggregation-fn {:optional true} :symbol]
   [:pricing/aggregation-filter-fn {:optional true} :any]
   [:pricing/billable [:vector :any]]
   [:pricing/charge-mode {:optional true} (into [:enum] charge-modes)]
   [:pricing/charges [:vector storage-flat-rate-charge]]])

(comment
  (m/explain terms-pricing
             (-> ductile.workflow.billing.sixt-infleet/sixt-infleet-contract-terms-2025-03-01
                 :contract-terms/pricings
                 (nth 19))))
(def terms-pricing
  (m/schema
   [:or service-terms-pricing storage-terms-pricing storage-flat-rate-terms-pricing transports-terms-pricing accessory-storage-pricing]))

(def billing-template-schema
  [:map {:closed true :label-key :billing-template/name :editor/auto-id {:ductile/id #(random-uuid)}}
   [:billing-template/name :string+]
   [:billing/customer billing-customer-reference-schema]
   [:billing/tax-rate {:optional true} tax-rate-schema]
   [:billing-template/bill-text {:optional true :editor/view :textarea} :string+]
   [:line-item/selection [:vector line-item-selection]]
   [:billing-template/email-config {:optional true} email-config-schema]
   [:billing-template/headers {:optional true} billing-header-schema]
   [:billing-template/csv-headers {:optional true} billing-header-schema]
   [:billing-template/item-order {:editor/readonly? true :optional true} item-order-schema]
   [:billing-template/locale {:optional true} (into [:enum] locales)]
   [:elog/billing-customer {:optional true} :string+]
   [:ductile/id {:editor/hidden? true} :uuid]
   [:billing-template/template-type {:editor/hidden? true :optional true} [:enum :internal-costs]]
   [:billing-template/invoice-suffix {:editor/hidden? true :optional true} :string+]])

(def contract-schema
  (m/schema [:map {:closed true}
             [:contract-terms/valid-from {:editor/view :date} :inst]
             [:billing/create-invoice-disabled? {:optional true}
              [:or :boolean [:string+ {:label (tr "Reason")}]]]
             [:ductile/compounds {:optional true :editor/view :checkbox-set} compounds-list-schema]
             [:contract-terms/contracts {:optional true} [:vector (into [:enum] contract-names)]] ;; TODO: fetch choice options instead
             [:billing-template/services {:optional true} [:vector service-pricing]]
             [:billing-template/accessory-services {:optional true} [:vector service-pricing]]
             [:billing-template/storage-settings {:optional true} storage-settings]
             [:billing-template/accessory-storage-settings {:optional true} accessory-storage-settings]
             [:billing/extra-items {:optional true} [:vector billing-extra-item-schema]]
             [:billing-template/entry-transport-settings {:optional true} transport-settings-schema]
             [:billing-template/exit-transport-settings {:optional true} transport-settings-schema]
             [:billing-template/exit-load-settings {:optional true} load-settings-schema-by-compound]
             [:contract/billing-templates [:vector billing-template-schema]]
             [:contract-terms/documents {:optional true} [:vector {:editor/view :cas-file} terms-document]]
             [:contract-terms/fuel-group {:optional true} (into [:enum] fuel-groups)]
             [:elog/billing-customer {:optional true} :string+]
             [:billing-template/postprocess {:optional true :editor/readonly? true} [:vector (into [:enum] postprocess-fn-symbols)]]
             [:contract/name {:editor/hidden? true} :string+]
             [:contract-terms/change {:editor/hidden? true :optional true} :uuid]
             [:billing/features {:editor/hidden? true :optional true} [:set billing-feature-schema]]
             [:contract-terms/contract-type {:editor/hidden? true :optional true} [:enum :contract.type/customer :contract.type/supplier]]
             [:contract-terms/pricings {:editor/hidden? true :optional true} [:vector terms-pricing]]]))

(comment
  (->> (ductile.workflow.billing/all-contracts)
       (map (fn [c] {:errors (:errors (m/explain contract-schema c))
                     :version (ductile.contract-terms/->local-date (:contract-terms/valid-from c))
                     :contract (:contract/name c)}))
       (filter :errors)
       first :errors first)

  (->> (ductile.workflow.billing/all-contracts)
       (filter :billing-template/bill-text)))

(def service-definition-schema
  [:map #_{:closed true}
   #_[:service/code :string+]
   #_[:service/name :string+]

   [:service/vehicle-preds {:optional true} preds-schema]
   [:service/contract-preds {:optional true} preds-schema]
   [:service/operational-preds {:optional true} preds-schema]

   [:service/schedule {:optional true} schedule-schema]
   [:service/required-scope {:optional true} scopes-schema]
   [:service/optionality {:optional true} [:enum
                                           :optionality/must
                                           :optionality/should
                                           :optionality/can]]

   #_#_[:service/kind {:optional true} [:enum :ductile.workflow.dsl/group]]
     [:service/services {:optional true} coll?]

   #_[:service/available-units {:optional true} [:set :keyword]]
   #_[:service/unit {:optional true} :keyword]

   #_[:service/postprocess {:optional true} :rec/fn]
   #_[:service/txd {:optional true} [:vector :any]]
   ;; custom lambda handler to call when `perform` is pressed
   ;; instead of `ductile.workflow/perform-service`
   #_[:service/on-perform {:optional true} :symbol]

   ;; detail text that shows next to the service name
   #_[:service/detail {:optional true} :string+]
   #_[:event/kind {:optional true} event-kind-schema]

   [:service/consequences {:optional true} [:sequential consequence-schema]] ;;sometimes list, sometimes vector
   [:ui/fields {:optional true} [:vector :any]]

   ;; effects to be created on completion of service, such as sending entry emails
   #_[:service/effects {:optional true} [:vector effect-schema]]

   [:service/scannable? {:optional true} :boolean]

   ;; This improves our arrival scan by making a backend call using a new
   ;; `ductile.workflow/fahrzeug-with-scan-match` lambda before marking a vehicle as
   ;; arrived. This allows folks to see the effect of a scan and correct it in
   ;; case the scan detected a wrong vin. This should hopefully reduce the
   ;; number of VIN corrections we need to manually perform.
   #_[:service/augment-scan? {:optional true} :boolean]

   #_[:service/take-first-child-service-as-active? {:optional true} :boolean]])

(register! :parking-zone/name :string+)

(def contract-workflow-definition-schema
  [:map
   [:contract/name :string+]
   [:contract/compounds {:description "Compounds where this contract is active"} [:set :compound/name]]
   [:fahrzeug/attributes {:description "Default attributes for all fahrzeuge in this contract"
                          :optional true} [:map-of :keyword :any]]
   [:contract/extra-txd {:optional true
                         :description "Function from fahrzeug->txd. Gets called and transacted when assigning this contract. Can be used for example to transact pending accessories."} :rec/fn]
   [:entry-inspection/fields {:description "Fields with attributes to ask for during entry inspection"
                              :optional true} [:vector :any #_ui-field-schema]]
   [:contract/special-equipment {:description "What equipment can be recorded at entry"
                                 :optional true} [:vector :string+]]
   [:contract/special-equipment-default {:optional true} [:set :string+]]
   [:parking-zone/default {:optional true} :parking-zone/name]
   [:contract/allow-manual-assignment? {:optional true
                                        :default true} :boolean]
   [:contract/equipment-removed-before-exit {:optional true} [:or
                                                              [:set :string+]
                                                              [:map-of :string+ :string+]]]
   [:contract/services {:description "Services in the workflow for this contract"
                        :default []
                        :optional true} [:vector service-definition-schema]]
   [:contract/extra-services {:description "Services that can be ordered by the office for this contract"
                              :default []
                              :optional true} [:vector :map
                                               ;;TODO: process-services on
                                               #_service-definition-schema]]])

(register! :ductile/note :string+)

(def price-sheet-definition-schema
  [:map
   [:price-sheet/contract :any]
   [:price-sheet/valid-from :inst]
   [:price-sheet/pricings [:set
                           [:map
                            [:service/code :string]
                            [:pricing/price :price/amount]
                            [:ductile/note]
                            #_[:pricing/conditional-prices [:vector [:map [:pricing/price :price/amount]
                                                                     [:ductile/note]]]]]]]])

(register! :entry/mode [:enum :mode/truck :mode/train :mode/own-wheels :mode/ship])

(register! :ductile/locale [:enum :en :de :it])

(register! :compound/module [:enum
                             :module/accessories
                             :module/base
                             :module/billing
                             :module/elog-interface
                             :module/truck-booking
                             :module/vin-list
                             :module/workflow])

(register! :ductile/timezone-string [:enum
                                     "Europe/Berlin"
                                     "Europe/Rome"])

(register! :time/day-of-week [:enum "monday" "tuesday" "wednesday" "thursday" "friday" "saturday" "sunday"])

;; EDI Mapping — configurable rule trees for contract assignment etc.
;; See notes/kommen/edi-mapping.md for the design.

(def edi-pred-fns
  "Qualified symbols of available predicate factory functions."
  ['ductile.predicates/field-in
   'ductile.predicates/field-prefix
   'ductile.predicates/field-matches-pattern])

(register! :condition/pred-fn (into [:enum] edi-pred-fns))
(register! :condition/field :keyword)
(register! :condition/values [:set :string+])
(register! :condition/pattern :string+)

(register! :ruleset/condition
           [:map
            [:condition/pred-fn]
            [:condition/field {:optional true}]
            [:condition/values {:optional true}]
            [:condition/pattern {:optional true}]
            [:ductile/note {:optional true} :string]])

(register! :rule/ordinal [:int {:min 0}])
(register! :rule/result [:tuple [:= :contract/name] (into [:enum] contract-names)])

(register! :ruleset/rule
           [:map
            [:rule/ordinal]
            [:rule/label {:optional true} :string+]
            [:rule/conditions [:vector :ruleset/condition]]
            [:rule/result]
            [:rule/valid-from {:optional true} :inst]
            [:rule/valid-until {:optional true} :inst]])

(register! :ruleset/purpose [:enum :ruleset.purpose/contract-assignment])
(register! :edi.mapping/sender [:enum :edi/eurolog.bmw :edi/kia :edi/sms-lidl :edi/omoda-jaecoo])

(register! :ruleset
           [:map
            [:ruleset/name :string+]
            [:edi.mapping/sender]
            [:ruleset/purpose]
            [:ruleset/rules [:vector :ruleset/rule]]])

(def compound-config
  (m/schema
   [:map {:closed? true}
    [:compound/entry-modes {:description "How cars can enter the compound"} [:set :entry/mode]]
    [:entry/truck-types {:description "Which kinds of trucks can show up at the compound"} [:set [:enum :entry.truck/ars :entry.truck/carrier]]]
    [:compound/legacy-arrival-scan {:optional true} :boolean]
    [:compound/enforce-loading-area-assignment? {:optional true} :boolean]
    ;;deprecated -> infer from :compound/address
    [:compound/locale {:optional true} :ductile/locale]
    ;;deprecated -> infer from :compound/address
    [:compound/timezone :ductile/timezone-string]
    [:compound/modules {:description "Which modules are enabled at the compound"} [:set :compound/module]]
    [:compound/elog-compound?
     {:optional true
      :description "Does this compound run ELOG (used to create routing announcements when dispatching to this compound)"}
     :boolean]
    ;;deprecated?
    [:compound/booking-slots {:optional true} [:int {:min 1}]]
    ;;deprecated?
    [:compound/unloading-lines {:optional true} [:int {:min 1}]]
    [:parking-zone/default {:optional true} :string+]
    [:parking/max-fahrzeuge-per-parking-spot {:optional true
                                              :min 1} :int]
    ;;deprecated?
    [:booking/new-vehicle-make-required? {:optional true} :boolean]
    ;;deprecated?
    [:compound/booking-slots+times {:optional true} [:map-of
                                                     :time/day-of-week
                                                     [:map-of [:tuple :int :int] :int]]]
    ;;deprecated?
    [:compound/extended-booking-slots+times {:optional true} [:map-of
                                                              :time/day-of-week
                                                              [:map-of [:tuple :int :int] :int]]]
    ;;deprecated?
    [:compound/special-slots+times {:optional true} [:map-of :string :map]]]))

#_(m/validate [:map-of :string compound-config] ductile.compound-config/compound-config)

(def service-list-spec-schema
  (m/schema
   [:map
    [:id :keyword]
    [:compounds [:set :compound/name]]
    [:ui [:map
          [:view :symbol]
          [:schema {:optional true} [:vector :keyword]]]]
    [:title :string+]
    [:icon :ui/icon]
    [:datomic/service-codes {:optional true} [:set :service/code]]
    [:datomic/query {:optional true} fn?]
    [:statistics {:optional true}
     [:map
      [:open {:description "Show statistics about open services"
              :optional true}
       [:map [:columns {:optional true} [:vector [:enum :service/code :contract/name]]]]]
      [:completed {:description "Show statistics about open services"
                   :optional true}
       [:map [:service/code {:description "Service codes"
                             :optional true} [:set :service/code]
              :timespan {:optional true
                         :default :today} [:enum :today :this-week]
              :columns {:optional true} [:vector [:enum :service/code :contract/name]]]]]]]]))
